
import { useState, useEffect, Fragment, useRef } from 'react';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { getUserPositionNonBlocking } from '@Components/Utilities/Geolocation';
import { cookieNames, categorySpecGroup, contactType, inquiryType, gtmEventType, LinkRelationTypes, maxSecondsAuctionTimeListingCanBeExtendedDueToBidding } from '@Components/Utilities/SandhillsConstants';
import { getDistinct, setWindowOverrides, sandhillsRequest, repositionCookieBanner, hasSecondaryPricing, addAuctionSiteDLVs, gtmPushEvent, setScrollPosition, unsetScrollPosition, logSAMError, criteoPushEvent, insertURLParameter, removeURLParameters, getURLParameter, showSiteJabber } from '@Components/Utilities/SandhillsUtility';
import { setBetaSiteCookie, clearCookie, getCookie, tryAddListingToCompareCookie, tryDeleteCompareListingFromCookie } from '@Components/Utilities/CookieHelper';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import { watchlistOnClick } from '@Components/Utilities/SandhillsWatchlist';
import ComplexBreadcrumb from '@Components/Shared/ComplexBreadcrumb';
import SelectField from '@Components/Shared/Controls/SelectField';
import TranslationText from '@Components/Utilities/TranslationText';
import ListingImage from '@Components/Listings/ListingImage';
import AuctionListingGroup from '@Components/Detail/AuctionListingGroup';
import MediaControl from '@Components/Shared/MediaControl';
import CategoryInformation from '@Components/Shared/CategoryInformation';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import ready from 'document-ready';
import translator from '@Components/Utilities/Translator';
import WatchListComment from '@Components/WatchList/WatchListComment';
import { ThemedButton, MediaButton, CheckAvailabilityButton, ThemedLinkButton } from '@Components/MuiWrappers/Button';
import { BaseTypography } from '@Components/MuiWrappers/Typography';
import { ThemedBox } from '@Components/MuiWrappers/ThemedBox';
import { ThemedLink } from '@Components/MuiWrappers/ThemedLink';
import { Link, Button, Icon, IconButton, Grid, Backdrop, CircularProgress } from '@mui/material';
import ConditionalWrapper from '@Components/Utilities/ConditionalWrapper';
import { ThemeProvider } from '@mui/material/styles';
import { setupBidCallerPreBiddingSignalRHub } from '@Components/Utilities/BidCallerPreBidding';
import { setupAuctionTimeBiddingSignalRHub, patchInUpdatedAuctionData } from '@Components/Utilities/AuctionTimeBidding';
import FleetEvaluatorValues from '@Components/Shared/FleetEvaluatorValues';
import BetaSiteCookieButton from '@Components/Shared/BetaSiteCookieButton';
import PaymentsAsLowAsLink from '@Components/Shared/FinancialCalculator/PaymentsAsLowAsLink';

const CompareCheckbox = loadable(() => import('@Components/Compare/CompareCheckbox'));
const MainDetailPricing = loadable(() => import('@Components/Detail/MainDetailPricing'));
const LoanApprovalEstimateDisclaimer = loadable(() => import('@Components/Shared/LoanApprovalEstimate/LoanApprovalEstimateDisclaimer'));
const LoanApprovalEstimateBanner = loadable(() => import('@Components/Shared/LoanApprovalEstimate/LoanApprovalEstimateBanner'));
const BadgeList = loadable(() => import('@Components/Shared/BadgeList'));
const TireTreadSpecs = loadable(() => import('@Components/Detail/TireTreadSpecs'));
const ShippingInfo = loadable(() => import('@Components/Detail/ShippingInfo'));
const ServiceLogs = loadable(() => import('@Components/Detail/ServiceLogs'));
const PayPalButton = loadable(() => import('@Components/Shared/Payments/PayPalButton'));
const InspectionList = loadable(() => import('@Components/Detail/InspectionList'));
const Brochure = loadable(() => import('@Components/Detail/Brochure'));
const AdditionalInformation = loadable(() => import('@Components/Detail/AdditionalInformation'));
const ExpiredListingWarning = loadable(() => import('@Components/Detail/ExpiredListingWarning'));
const FloorPlan = loadable(() => import('@Components/Listings/FloorPlan'));
const WidgetList = loadable(() => import('@Components/Shared/Widgets/WidgetList'));
const CalculatorCurrencyBtn = loadable(() => import('@Components/Shared/FinancialCalculator/CalculatorCurrencyBtn'));
const DealerInfo = loadable(() => import('@Components/Detail/DealerInfo'));
const LoanApprovalEstimateModal = loadable(() => import('@Components/Shared/Modals/LoanApprovalEstimateModal'), true);
const EmployeeListingModal = loadable(() => import('@Components/Shared/Modals/EmployeeListingModal'), true);
const EmailModal = loadable(() => import('@Components/Shared/Modals/EmailModal'), true);
const CalculatorModal = loadable(() => import('@Components/Shared/Modals/CalculatorModal'), true);
const StaticACCReport = loadable(() => import('@Components/Detail/StaticACCReport'));
const BuyersPremiumModal = loadable(() => import('@Components/Shared/Modals/BuyersPremiumModal'), true);
const BuyersPremiumForm = loadable(() => import('@Components/Forms/BuyersPremiumForm'));
const DetailBiddingBox = loadable(() => import('@Components/Detail/DetailBiddingBox'));
const MobileModalControl = loadable(() => import('@Components/Shared/Modals/MobileModalControl'));
const DetailRequestAQuoteBox = loadable(() => import('@Components/Detail/DetailRequestAQuoteBox'));
const WatchlistExpansion = loadable(() => import('@Components/Shared/Listings/WatchlistsExpansion'));
const OptionsPopper = loadable(() => import('@Components/Shared/Listings/OptionsPopper'));
const BidHistoryModal = loadable(() => import('@Components/Shared/Modals/BidHistoryModal'), true);
const WinningBidderModal = loadable(() => import('@Components/Shared/Modals/WinningBidderModal'), true);
const BetaSiteCookieWarning = loadable(() => import('@Components/Shared/BetaSiteCookieWarning'));
const DetailRequestACharterBox = loadable(() => import('@Components/Detail/DetailRequestACharterBox'));

const DetailsView = (props) => {

    const auctionInformationTimeout = useRef(0);
    const vip = sandhills.TradeSites.React.ComponentProps['vip'];
    const currentUser = useRecoilValue(atoms.currentUser);
    const windowSize = useWindowSize();
    const lineBreakTerms = ["\r", "\n", "<br/>", "<br>", "<br />"];
    const isTermsAndConditionsMultiline = lineBreakTerms.some(term => props?.TermsAndConditions?.indexOf(term) > -1);
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const routes = useRecoilValue(atoms.routes);
    const isRY = currentSite?.Site?.IsRentalYard;
    const theme = useRecoilValue(atoms.muiThemes);
    const [useBetaSiteCookie, setUseBetaSiteCookie] = useState(false);
    const showEmployeeOnlyBetaSiteToggle = useRecoilValue(atoms.showEmployeeOnlyBetaSiteToggle);
    const showEmailTextOnly = isRY || currentSite?.Site?.IsCharterHub;
    const [hasLoggedImpressions, setHasLoggedImpression] = useState(false);

    useEffect(() => {
        if (getCookie(cookieNames.useBetaSiteCookie) == '1') {
            setUseBetaSiteCookie(true);
        }
        if (currentSite?.Site?.IsHostedOrFramelessSite && !hasLoggedImpressions && !window.location.href.includes("sandhillsinventory")) {
            setHasLoggedImpression(true);
            sandhillsRequest({
                url: routes.DetailImpression,
                method: 'post',
                data: {
                    ListingID: props.ListingID,
                    baseCatID: props.BaseCatID,
                    categoryID: props.CategoryInformation.CategoryID,
                    HSPub: currentUser.HSTrackingUserID,
                    qfc: props.QFC,
                    shortQFC: props.ShortQFC
                }
            });
        }

    }, []);

    const toggleBetaSiteCookie = () => {
        const newUseBetaSiteCookie = !useBetaSiteCookie;
        setUseBetaSiteCookie(newUseBetaSiteCookie);
        if (newUseBetaSiteCookie) {
            setBetaSiteCookie('1');
            window.location.reload();
        }
        else {
            clearCookie(cookieNames.useBetaSiteCookie);
            window.location.reload();
        }
    };

    useEffect(() => {
        if (!(props.ListingID && props.ListingID > 0 || props.SAMModel?.DealerGroupID && props.SAMModel?.DealerGroupID > 0)) {
            logSAMError('Listing has no ID or no DealerGroup ' + window.location.href);
        }
    }, []);

    const phoneClickToTrack = (e, phoneNumber, phoneType) => {
        if (!currentUser.IsSearchEngine) {
            let element = e.target;
            sandhillsRequest({
                url: props.ContactInfo.PhoneClickToTrackURL,
                method: 'post',
                data: {
                    PhoneNumber: phoneNumber,
                    PhoneType: phoneType
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then(() => {
                element.setAttribute('data-impression-logged', 'true');
            }).catch(() => {
                element.setAttribute('data-impression-logged', 'false');
            });
        }
    };

    const getGeneralSpecs = () => {
        let generalSpecs = null;
        if (props.Specs && props.Specs.length > 0) {
            props.Specs.map(spec => {
                if (spec.SpecGroupID == categorySpecGroup.General) {
                    generalSpecs = spec.Specs;
                }
            });
        }
        return generalSpecs;
    };

    const getCategorySpecs = () => {
        let categorySpecs = [];
        if (props.Specs && props.Specs.length > 0) {
            props.Specs.map(spec => {
                if (spec.SpecGroupID != categorySpecGroup.General) {
                    categorySpecs.push(spec);
                }
            });
        }
        return categorySpecs;
    };

    const getInitialSelectedInquiry = () => {
        if (props.SAMModel && (props.SAMModel.ShowSAMOnLoad || props.SAMModel.ShowSAMWOnLoad)) {
            return inquiryType.Email;
        }

        if (props.SAMModel && props.SAMModel.ShowMAOOnLoad) {
            return inquiryType.Offer
        }

        return '';
    };

    const getTermsAndConditionsFirstLine = () => {
        if (props.TermsAndConditions && props.AuctionDetails && isTermsAndConditionsMultiline) {
            let lineBreakIndices = lineBreakTerms.map(term => props.TermsAndConditions.indexOf(term)).filter(i => i > -1);
            return props.TermsAndConditions.substring(0, Math.min(...lineBreakIndices));
        }
        return null;
    };

    const [showShareButtons, setShowShareButtons] = useState(false);
    const [showFinancialCalculator, setShowFinancialCalculator] = useState(false);
    const [shippingEstimateData, setShippingEstimateData] = useState(null);
    const [showEmailModal, setShowEmailModal] = useState(props.SAMModel && (props.SAMModel.ShowSAMOnLoad || props.SAMModel.ShowMAOOnLoad));
    const [selectedInquiry, setSelectedInquiry] = useState(getInitialSelectedInquiry());
    const [selectedContactType, setSelectedContactType] = useState(contactType.Retail);
    const [showEmployeeModal, setShowEmployeeModal] = useState(false);
    const [dataAction, setDataAction] = useState('add');
    const [isSavedToWatchlist, setIsSavedToWatchlist] = useState(false);
    const [watchListComment, setWatchListComment] = useState('');
    const [paypalSdkLoaded, setPaypalSdkLoaded] = useState(false);
    const [compareIsChecked, setCompareIsChecked] = useState(props.IsCheckedForCompare);
    const imageCarouselClass = 'media-container ' + (props.MediaModel.ShowThumbnails && props.MediaModel.Media.length > 0 ? '' : 'media-container_empty');
    const generalSpecs = getGeneralSpecs();
    const categorySpecs = getCategorySpecs();
    const [userLocation, setUserLocation] = useState({
        longitude: currentUser?.CurrentLocation?.Coordinates?.Longitude,
        latitude: currentUser?.CurrentLocation?.Coordinates?.Latitude
    });
    const [showBuyersPremium, setShowBuyersPremium] = useState(false);
    const [auctionBiddingData, setAuctionBiddingData] = useState(props.AuctionDetails?.AuctionBiddingData);
    // For the AT Updates, the State property did not always have the most recent data? Utilizing a ref strictly for that real time update usage
    const latestAuctionBiddingData = useRef(props.AuctionDetails?.AuctionBiddingData);
    const [quickBiddingEnabled, setQuickBiddingEnabled] = useState(props.AuctionDetails?.IsQuickBidEnabled ?? false);
    const [quickBiddingError, setQuickBiddingError] = useState(false);
    const [fitsTo, setFitsToExpanded] = useState(props.GroupedInfo?.map?.(fitsTo => {
        return {
            ...fitsTo,
            Expanded: false
        };
    }) || []);
    const returnFocusRef = useRef([]);
    const [ref, setRef] = useState(null);
    const [samModel, setSamModel] = useState(props.SAMModel);
    const [bidHistoryData, setBidHistoryData] = useState(null);
    const [showBidHistoryModal, setShowBidHistoryModal] = useState(false);
    const [winningBidderData, setWinningBidderData] = useState(null);
    const [showWinningBidderModal, setShowWinningBidderModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const hasVariants = props.VariantOptions?.length > 0 ? true : false;

    const variantOptions = hasVariants ? props.VariantOptions : null;
    const variants = hasVariants ? props.Variants : null;
    const [selectedVariantOptionValues, setSelectedVariantOptionValues] = useState([]);
    const [variant, setVariant] = useState(null);
    const [outOfStock, setOutOfStock] = useState(false);
    const [buyNowURL, setBuyNowURL] = useState(props.BuyNowModel.BuyNowURL)
    const [displayWatchlistExpansion, setDisplayWatchlistExpansion] = useState(false);
    const [watchlistChecked, setWatchlistchecked] = useState(null);
    const [expansionFocus, setExpansionFocus] = useState(false);
    const [similarListings, setSimilarListings] = useState([]);
    const [termsAndConditions, setTermsAndConditions] = useState(getTermsAndConditionsFirstLine() || props.TermsAndConditions);
    const [isPreATModal, setIsPreATModal] = useState(false);
    const [isWholesaleModal, setIsWholesaleModal] = useState(props.SAMModel?.ShowSAMWOnLoad);
    const [useWholesaleOverride, setUseWholesaleOverride] = useState(props.SAMModel?.ShowSAMWOnLoad);
    const [countdownListings, setCountdownListings] = useState([]);
    const [timeExtended, setTimeExtended] = useState(false);
    const latestTimeRemaining = useRef(auctionBiddingData?.TimeRemaining);

    const [showGroupExtensionNote, setshowGroupExtensionNote] = useState(props.AuctionDetails?.IsAT && !props.AuctionDetails?.IsAuctionResult && props.AuctionDetails?.AuctionListingGroupListings?.length > 0 && auctionBiddingData?.TimeRemaining < 3600);
    const [hasAuctionListingGroupListings, setHasAuctionListingGroupListings] = useState(props.AuctionDetails?.IsAT && props.AuctionDetails?.AuctionListingGroupListings?.length > 0);

    const waitingForQuickBidAttemptResponse = useRef(false);
        
    useEffect(() => {
        setWindowOverrides();
        const cookieBannerResize = () => {
            repositionCookieBanner('detail-contact-bar', 1001);
        };

        ready(() => {
            cookieBannerResize();
            window.addEventListener('resize', cookieBannerResize);
        });

        if (props.GetShippingEstimate && !shippingEstimateData && !userLocation.longitude && !userLocation.latitude) {
            getUserPositionNonBlocking(true)
                .then((response) => {
                    if (response) {
                        setUserLocation({ latitude: response.latitude, longitude: response.longitude });
                    }
                });
        }
        if (props.BidCallerPreBiddingHubUrl.trim() !== "") {
            setupBidCallerPreBiddingSignalRHub(props.BidCallerPreBiddingHubUrl, props.ShowEmployeeOnlyInfo, props.ListingID.toString(), resetAuctionData);
        }
        if (props.AuctionTimeBiddingHubUrl.trim() !== "" && auctionBiddingData?.EventID && auctionBiddingData?.IsAuctionLive) {
            setupAuctionTimeBiddingSignalRHub(props.AuctionTimeBiddingHubUrl, props.ShowEmployeeOnlyInfo, currentSite?.IsMobileAppContext, auctionBiddingData.AuctionUpdateKey, updateLatestBiddingData, updateBiddingPausedStatus);
        }
        if (props.ShouldPushCriteoEvent) {
            criteoPushEvent("viewItem", null, props.ListingID);
        }

        setVariantIdFromSelectedOptionValues(selectedVariantOptionValues);

        if (currentUser.IsAuthenticated && !currentSite?.Site?.IsHostedSite) {
            sandhillsRequest({
                url: routes.GetWatchList + '/' + (props.ListingID ? props.ListingID : ''),
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((response) => {
                if (response && response[0]) {
                    let listing = response[0];
                    setWatchListComment(listing.Comment);
                    setWatchListAction(listing.ListingId == props.ListingID);
                    setWatchlistchecked(listing);
                }
            });
        }

        if (!currentSite.IsFramelessSite && props.ShowSimilarListings) {
            populateSimilarListings();
        }
        if (props.SAMModel?.ShowSAMWOnLoad) {
            openThisEmailModal(inquiryType.Email, props.contactType, null, isWholesaleModal, props.HasPreATData);
        }

        if (window && auctionBiddingData) {
            if (auctionBiddingData.UserBidInfo) {
                const bidInfo = auctionBiddingData.UserBidInfo;
                const userHighBidder = !bidInfo.UserIsOutBid && !bidInfo.UserIsTieBidder && bidInfo.ListingBidStatusText;

                if (getURLParameter(window.location.href, "AuctionEnded") && userHighBidder && !props.AuctionDetails?.IsLiveEvent && !sessionStorage.getItem('siteJabberShown')) {
                    showSiteJabber(currentUser, props.ListingID);
                }

            }
        }

        addAuctionDLVs();

        return () => {
            window.removeEventListener('resize', cookieBannerResize);
        };
    }, []);

    useEffect(() => {
        if (variant) {
            setBuyNowURL(`${props.BuyNowModel.BuyNowURL}&variantId=${variant.VariantID}`);
        }
    }, [variant]);

    useEffect(function shippingEstimation() {
        if (userLocation.latitude && userLocation.longitude && props.GetShippingEstimate) {
            sandhillsRequest({
                url: routes.FR8StarURL,
                method: 'get',
                data: {
                    listingIDs: props?.ListingID?.toString(),
                    isDetailPage: true
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((data) => {
                if (data && data.success == true) {
                    setShippingEstimateData(data);
                }
            });
        }
    }, [userLocation]);
       
    const populateSimilarListings = () => {
        sandhillsRequest({
            url: routes.GetSimilarListings,
            method: 'get',
            data: { Category: props.CategoryInformation.CategoryID, Manufacturer: props.Manufacturer, Model: props.Model, ForceGetSimilar: true, ListingID: props.ListingID, sellerAccountCRMID: props.SimilarListingsSellerAccountCRMID, ShowRecSysForEmployee: props.EmployeeRecSysSearch },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            setSimilarListings(response?.SimilarListings);
        })
    }

    const handleVariantUpdate = (value, optionName) => {
        const associatedVariant = variants.find(v => v.OptionValues.some(ov => ov.OptionName == optionName && ov.VariantOptionValue == value));
        const selectedVariantOptionValue = associatedVariant?.OptionValues.find(ov => ov.VariantOptionValue == value);

        if (selectedVariantOptionValues) {
            let index = selectedVariantOptionValues.findIndex((x) => x.OptionName == selectedVariantOptionValue.OptionName);

            if (index === -1) {
                index = 0;
            }

            selectedVariantOptionValues[index] = selectedVariantOptionValue;
            setSelectedVariantOptionValues(selectedVariantOptionValues);
            setVariantIdFromSelectedOptionValues(selectedVariantOptionValues);
        }
    };

    const setVariantIdFromSelectedOptionValues = (array) => {
        if (variants) {
            loop1: for (var vi = 0; vi < variants.length; vi++) {
                let isMatch = true;

                loop2: for (var i = 0; i < array.length; i++) {
                    if (variants[vi]?.OptionValues[i]?.OptionName != array[i]?.OptionName || variants[vi]?.OptionValues[i]?.VariantOptionValue != array[i]?.VariantOptionValue) {
                        isMatch = false;
                        break loop2;
                    }
                }

                if (isMatch) {
                    setVariant(variants[vi]);
                    setOutOfStock(variants[vi].Quantity <= 0 || variants[vi].Status == 'INACTIVE' ? true : false);

                    break loop1;
                }
                else if (vi + 1 == variants.length) {
                    setSelectedVariantOptionValues(array);
                    setVariant(null);
                    setOutOfStock(true);
                }
            }
        }
    };

    const matchPreviousSelected = (selectedVariantOptionValues, currentVariant, index) => {
        let isMatch = true;

        while (index > 0) {
            let previousVariant = selectedVariantOptionValues[index - 1];

            if (previousVariant) {
                isMatch = isMatch && currentVariant.OptionValues.some(ov => ov.VariantOptionValue == previousVariant.VariantOptionValue);
            }
            index--;
        }

        return isMatch;
    };

    const handleEnterKeyPressForVariations = (e, variantOptionValue, optionName) => {
        if (e.code === 'Enter') {
            handleVariantUpdate(variantOptionValue, optionName);
        }
    }

    const toggleWatchlistExpansion = (count) => {
        if (!displayWatchlistExpansion) {
            setIsSavedToWatchlist(true);
        }
        if (count == 0) {
            setIsSavedToWatchlist(false);
        }
        setDisplayWatchlistExpansion(!displayWatchlistExpansion);
    }
    const forceExpansion = () => {
        if (!displayWatchlistExpansion && !isSavedToWatchlist) {
            setExpansionFocus(true);
            setDisplayWatchlistExpansion(true);
            setIsSavedToWatchlist(true);
            watchlistClick();
        } else {
            setDisplayWatchlistExpansion(false);
            watchlistOnClick('delete', props.ListingID, [props.ListingType], routes);
            setWatchListAction(dataAction == 'delete');
            setIsSavedToWatchlist(false);
        }
    }

    const renderVariantOptions = () => {
        const isTile = props.ProductVariationDisplay == 'Tile' || props.ProductVariationDisplay == null ? true : false;

        return variantOptions.map((option, index) => {
            let selectListValues = [];

            return (
                <div className='variation'>
                    <div>
                        <div className='option-name'>
                            <span>{option.OptionName}</span>
                        </div>
                        <div className={isTile ? 'variation-selection-values' : ''}>
                            {
                                option.OptionValues.map(optionValue => {
                                    let associatedVariant = variants.find(v => v.OptionValues.some(ov => ov.OptionName == option.OptionName && ov.VariantOptionValue == optionValue.Value) && v.Quantity > 0 && matchPreviousSelected(selectedVariantOptionValues, v, index));

                                    if (associatedVariant) {
                                        let associatedVariantOptionValue = associatedVariant.OptionValues.find(ov => ov.VariantOptionValue == optionValue.Value);

                                        if (!isTile) {
                                            selectListValues.push({
                                                Value: optionValue.Value,
                                                Text: optionValue.Value,
                                                Disabled: false
                                            });
                                        }

                                        if (!selectedVariantOptionValues[index] || selectedVariantOptionValues[index].VariantOptionValue == associatedVariantOptionValue.VariantOptionValue) {
                                            if (!selectedVariantOptionValues[index]) {
                                                // only push if the value doesn't exist
                                                selectedVariantOptionValues.push(associatedVariantOptionValue);
                                            }
                                            // Is Selected
                                            if (isTile) {
                                                return <a className='active' role='button' tabIndex={0} onKeyDown={(e) => handleEnterKeyPressForVariations(e, associatedVariantOptionValue.VariantOptionValue, option.OptionName)} onClick={() => handleVariantUpdate(associatedVariantOptionValue.VariantOptionValue, option.OptionName)}>{optionValue.Value}</a>
                                            }
                                        }
                                        else {
                                            // Is available
                                            if (isTile) {
                                                return <a className='inactive' role='button' tabIndex={0} onKeyDown={(e) => handleEnterKeyPressForVariations(e, associatedVariantOptionValue.VariantOptionValue, option.OptionName)} onClick={() => handleVariantUpdate(associatedVariantOptionValue.VariantOptionValue, option.OptionName)}>{optionValue.Value}</a>
                                            }
                                        }
                                    }
                                    else {
                                        associatedVariant = variants.find(v => v.OptionValues.some(ov => ov.OptionName == option.OptionName && ov.VariantOptionValue == optionValue.Value) && matchPreviousSelected(selectedVariantOptionValues, v, index));

                                        if (associatedVariant) {
                                            let associatedVariantOptionValue = associatedVariant.OptionValues.find(ov => ov.VariantOptionValue == optionValue.Value);

                                            if (selectedVariantOptionValues[index] && selectedVariantOptionValues[index].VariantOptionValue == associatedVariantOptionValue.VariantOptionValue) {
                                                if (isTile) {
                                                    return <a className='active disabled'>{optionValue.Value}</a>
                                                }
                                                else {
                                                    selectListValues.push({
                                                        Value: optionValue.Value,
                                                        Text: optionValue.Value + ' - Unavailable',
                                                        Disabled: true
                                                    });
                                                }
                                            }
                                            else {
                                                // Disabled
                                                if (isTile) {
                                                    return <a className='disabled'>{optionValue.Value}</a>
                                                }
                                                else {
                                                    selectListValues.push({
                                                        Value: optionValue.Value,
                                                        Text: optionValue.Value,
                                                        Disabled: true
                                                    });
                                                }
                                            }
                                        }
                                        else {
                                            // No variant were found in either case.
                                            if (!variant) {

                                                if (selectedVariantOptionValues[index]?.VariantOptionValue == optionValue.Value) {
                                                    if (isTile)
                                                        return <a className='active disabled'>{optionValue.Value}</a>
                                                    else
                                                        selectListValues.push({
                                                            Value: optionValue.Value,
                                                            Text: optionValue.Value + ' - Unavailable',
                                                            Disabled: true
                                                        });
                                                }
                                            }
                                        }
                                    }
                                })
                            }
                            {
                                !isTile ?
                                    <SelectField
                                        key={option.OptionName + '_OptionName'}
                                        id={option.OptionName + '_OptionName'}
                                        name={option.OptionName + '_OptionName'}
                                        value={selectedVariantOptionValues.find(y => y.OptionName == option.OptionName).VariantOptionValue}
                                        options={selectListValues}
                                        onFieldChanged={(e) => handleVariantUpdate(e.target.value, option.OptionName)}
                                        label='Select Option'
                                        hideLabel
                                    /> : <></>
                            }
                        </div>
                    </div>
                </div>
            );
        });
    }

    const toggleTermsAndConditions = () => {
        if (termsAndConditions) {
            if (termsAndConditions.length < props.TermsAndConditions.length) {
                //we are viewing the short version, show the rest
                setTermsAndConditions(props.TermsAndConditions);
            }
            else {
                //we are looking at the full text, shorten it
                setTermsAndConditions(getTermsAndConditionsFirstLine());
            }
        }
    };

    const setWatchListAction = (isSaved) => {
        if (isSaved) {
            setDataAction('delete');
            setIsSavedToWatchlist(true);
        } else {
            setDataAction('add');
            setIsSavedToWatchlist(false);
        }
    };

    const watchlistClick = () => {
        if (!isSavedToWatchlist) {
            watchlistOnClick(dataAction, props.ListingID, [props.ListingType], routes);
        }
    };

    const expandFitsTo = (item) => {
        setFitsToExpanded(previousState => {
            return [...previousState].map(fitsTo => {
                if (fitsTo.Key == item.Key) {
                    return { ...fitsTo, Expanded: !item.Expanded };
                } else {
                    return fitsTo;
                }
            });
        });
    };

    const toggleEmployeeModal = () => {
        if (!showEmployeeModal) {
            setScrollPosition();
        } else {
            unsetScrollPosition();
        }
        setShowEmployeeModal(showEmployeeModal => !showEmployeeModal);
    };

    const updateCompareListing = (e) => {
        let mediaId = props.DisplayImageMediaId || 0;
        if (!compareIsChecked && tryAddListingToCompareCookie(props.ListingID, mediaId)) {
            setCompareIsChecked(true);
        } else if (compareIsChecked && tryDeleteCompareListingFromCookie(props.ListingID)) {
            setCompareIsChecked(false);
        }
    };

    const toggleCalculator = () => {
        if (returnFocusRef.current['finCalc']) {
            returnFocusRef.current['finCalc'].focus();
        } else {
            window.location = window.location.href + '#';
        }
        setShowFinancialCalculator(showFinancialCalculator => !showFinancialCalculator);
    };

    const onEmailModalToggle = () => {
        if (ref) {
            ref?.focus();
        } else {
            window.location = window.location.href + '#';
        }
        setShowEmailModal(showEmailModal => !showEmailModal);
        setIsPreATModal(false);
        setIsWholesaleModal(false);
        setUseWholesaleOverride(false);
    };

    const fireSendAMessageError = (inquiry, contact, data) => {

        const errorData = {
            CRMID: data.DealerCRMID,
            DealerGroupID: data.DealerGroupID,
            ListingBaseCatID: data.ListingBaseCatID,
            ListingCategoryID: data.ListingCategoryID,
            ListingID: data.ListingID,
            ListingManufacturer: data.ListingManufacturer,
            ListingModel: data.ListingModel,
            ListingSourceDomainUrl: data.ListingSourceDomainUrl,
            ListingURL: data.ListingURL,
            ModalTitle: data.ModalTitle,
            Subject: data.Subject
        }

        let msg = `SAM Error in DetailsView.jsx: \r\nInquiry: ${inquiry} \r\nContact: ${contact} \r\nData: ${errorData ?? JSON.stringify(errorData)}`;
        logSAMError(msg);
    }

    const openBidHistoryModal = () => {
        if (!isLoading) {
            setIsLoading(true);
            sandhillsRequest({
                url: routes.GetBidHistory + "/" + props.ListingID,
                errorHandling: {
                    message: 'An error occurred while retrieving the Bid History. Please refresh your page and try again.',
                    useConsole: false,
                    useAlert: true
                }
            }).then((data) => {
                setIsLoading(false);
                if (data) {
                    setBidHistoryData(data);
                    setShowBidHistoryModal(true);
                }
                else {
                    alert('An error occurred while retrieving the Bid History. Please refresh your page and try again.');
                }
            }).catch((error) => {
                setIsLoading(false);
            });
        }
    };

    const openWinningBidderModal = () => {
        if (!isLoading) {
            setIsLoading(true);
            sandhillsRequest({
                url: routes.GetWinningBidder + "/" + props.ListingID,
                errorHandling: {
                    message: 'An error occurred while retrieving the Bid Winner. Please refresh your page and try again.',
                    useConsole: false,
                    useAlert: true
                }
            }).then((data) => {
                setIsLoading(false);
                if (data) {
                    setWinningBidderData(data);
                    setShowWinningBidderModal(true);
                }
                else {
                    alert('An error occurred while retrieving the Bid Winner. Please refresh your page and try again.');
                }
            }).catch((error) => {
                setIsLoading(false);
            });
        }
    };

    const openThisEmailModal = (inquiry, contact, modalRef, isWholeSale, isPreAT, isPreTrade) => {
        if (inquiry != inquiryType.EmailThis && (!props?.DealerGroupID || props?.DealerGroupID == 0) && (!props?.ListingID || props?.ListingID == 0)) {
            fireSendAMessageError(inquiry, contact, props);
        }

        if ((contact != contactType.Wholesale) && (contact != contactType.ExclusiveWholesale) && (contact != contactType.Rental)) {
            contact = contactType.Retail;
        }
        setIsWholesaleModal(isWholeSale || isPreAT || isPreTrade);
        setIsPreATModal(isPreAT);
        setUseWholesaleOverride(isWholeSale || isPreAT || isPreTrade);

        modalRef = modalRef || null;

        if (samModel && samModel.ListingID > 0) {
            getFormAssembly(samModel);
        }

        if (inquiry == inquiryType.EmailThis) {
            if (modalRef) {
                modalRef.ModalTitle = translator.translate('sandhillssystem.share').toString();
            }


            if (windowSize.isMobile && navigator.share) {
                navigator.share({
                    title: document.title,
                    url: window.location.href
                })
                    .then(() => gtmPushEvent(gtmEventType.MobileShare))
                    .catch(() => setShowShareButtons(true));
                return;
            } else {
                setShowShareButtons(true);
            }

        } else {
            if (modalRef) {
                modalRef.ModalTitle = props.DisplayedListingTitle || props.ListingTitle;
            }
            setShowShareButtons(false);
        }

        if (modalRef) {
            setTimeout(() => {
                setRef(modalRef);
            }, 150);
        } else {
            setTimeout(() => {
                setRef(returnFocusRef.current[inquiry]);
            }, 150);
        }

        setSelectedInquiry(inquiry);
        setSelectedContactType(contact);
        setShowEmailModal(showEmailModal => !showEmailModal);
        setSamModel(previousState => {
            return {
                ...previousState,
                ...modalRef
            };
        });
    };

    const saveIterableAction = (actionName) => {
        sandhillsRequest({
            url: routes.SaveAction,
            method: 'post',
            data: {
                'email': '',
                'actionName': actionName,
                'manufacturers': [props.Manufacturer],
                'models': [props.Model],
                'listingTypes': [props.ListingType],
                'categoryIDs': getDistinct([props.CategoryInformation.CategoryID, props.CategoryInformation.AltCategoryID]),
                'listingIDs': [props.ListingID],
                'conditions': [props.Condition]
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        });
    };

    const handleBuyNowClick = () => {
        saveIterableAction('buyNow');
        gtmPushEvent(gtmEventType.buyNowClick);
    };

    const resetAuctionData = (listingID, addedToWatchlist, bidResult) => {
        let auctionDataURL = routes.GetAuctionData;

        if (bidResult) {
            if (!waitingForQuickBidAttemptResponse.current) {  
                // If we're returning the bid result, the most current data will otherwise be pushed to the client via SignalR
                const updatedData = { ...latestAuctionBiddingData.current, UserBidInfo: bidResult };
                setAuctionBiddingData(updatedData);
                latestAuctionBiddingData.current = updatedData;
                return;
            } else {
                //Add to query string so we can track when this occurs
                auctionDataURL = insertURLParameter(auctionDataURL, 'BidResultMissingPush', 1);
            }
        }

        sandhillsRequest({
            url: auctionDataURL,
            data: props.AuctionDetails.BiddingSearchModel,
            method: 'post'
        }).then((data) => {
            if (data?.EquipmentFactsResult?.length > 0) {
                setAuctionBiddingData(data.EquipmentFactsResult?.[0]);
                if (addedToWatchlist && !isSavedToWatchlist) {
                    setWatchListAction(false);
                }
            }
            else if (data?.AuctionTimeResult?.length > 0) {
                setAuctionBiddingData(data.AuctionTimeResult?.[0]);
                latestAuctionBiddingData.current = data.AuctionTimeResult?.[0];
                if (addedToWatchlist && !isSavedToWatchlist) {
                    setWatchListAction(false);
                }
                setWaitingForQuickBidAttemptResponseFlag(false);                
            }
            else {
                console.log('An error occurred while attempting to update bidding price.');
            }
        }).catch((error) => {
            console.log(error);
        }).finally(() => {
            if (waitingForQuickBidAttemptResponse.current) {
                setWaitingForQuickBidAttemptResponseFlag(false);
            }
        });
    };

    const setWaitingForQuickBidAttemptResponseFlag = (submittedQuickBid) => {
        waitingForQuickBidAttemptResponse.current = submittedQuickBid;
    }

    const setQuickBidValues = (quickBidEndabled, quickBidError) => {
        setQuickBiddingEnabled(quickBidEndabled);
        setQuickBiddingError(quickBidError);
    }

    let errorCount = 0;

    const addAuctionDLVs = () => {
        if (currentSite?.Site?.IsAuctionSite) {
            let eventId = '';
            let eventName = '';
            let sellerName = '';
            let listingId = props.AuctionBiddingData?.ListingID || props.ListingID;
            let sellerId = props.ContactInfo?.ParentAccountCRMID;
            let userID = currentUser.CRMContactID;
            if (props.AuctionDetails?.IsAT) {
                eventId = props.StandaloneSaleID || props.WeeklyAuctionGroupID;
                eventName = props.AuctionGroupName
                sellerName = props.ContactInfo?.CompanyName;
            } else if (props.AuctionDetails?.IsEF) {
                eventId = props.AuctionBiddingData?.EventID;
                eventName = props.AuctionBiddingData?.EventTitle;
                sellerName = props.AuctioneerName;
            }

            addAuctionSiteDLVs({
                listingId: listingId,
                eventId: eventId,
                eventName: eventName,
                sellerId: sellerId,
                sellerName: sellerName,
                userID: userID
            });
        }
    }

    const auctionHasEnded = () => {
        let currentURL = window.location.href;
        let newURL = currentURL.replace("upcoming-auctions", "auction-results");

        // If this is triggered multiple times, this will increment the AuctionEnded parameter
        let currentValue = parseInt(getURLParameter(newURL, "AuctionEnded"), 10) || 0;
        newURL = insertURLParameter(newURL, "AuctionEnded", currentValue + 1);

        // Add QS param to indicate what banner was shown to the user
        newURL = appendCurrentBannerQS(newURL);
        window.location = newURL;
    }

    const fetchAuctionData = () => {
        if (countdownListings?.length > 0) {
            sandhillsRequest({
                url: appendCurrentBannerQS(routes.AuctionUpdatesGetUpdate),
                method: 'POST',
                data: {
                    Keys: countdownListings.map(x => x.auctionBiddingData.AuctionUpdateKey)
                },
                errorHandling: {
                    message: 'Error retrieving auction data',
                    showDetails: true,
                    useConsole: true,
                    useAlert: false
                }
            }).then(response => {
                const data = response;
                // Note - AuctionUpdates does not return an array when BiddingInputPaused is true
                if (data?.AuctionUpdates?.length > 0 || data.BiddingInputPaused) {
                    errorCount = 0; // Reset error count on successful response

                    updateLatestBiddingData(data.AuctionUpdates, data.BiddingInputPaused);
                }
                else if (response === null || response === "") {
                    errorCount++;
                    console.log("Countdown Error count = ", errorCount);
                    if (errorCount % 2 === 0) {
                        let currentURL = window.location.href;
                        let reloadCount = parseInt(getURLParameter(currentURL, "InfoRequestFailure"), 10) || 0;
                        reloadCount += 1;
                        window.location.href = insertURLParameter(currentURL, "InfoRequestFailure", reloadCount); // Refresh the page if there are 2 consecutive errors
                    }
                }
            }).catch(error => {
                console.error(`Error updating listing:`, error);

            }).finally(() => {
                if (countdownListings.length > 0) {
                    auctionInformationTimeout.current = setTimeout(fetchAuctionData, 5000);
                }
            });
        }
    };

    const appendCurrentBannerQS = (newURL) => {
        // Add QS param to indicate what banner was shown to the user
        newURL = removeURLParameters(newURL, ["tbb", "obb", "hbb", "qbeb"]);
        if (auctionBiddingData?.UserBidInfo?.UserIsTieBidder) {
            newURL = insertURLParameter(newURL, "tbb", "1");
        }
        else if (auctionBiddingData?.UserBidInfo?.UserIsOutBid) {
            newURL = insertURLParameter(newURL, "obb", "1");
        }
        else if (auctionBiddingData?.UserBidInfo?.UsersLastBid > 0) {
            newURL = insertURLParameter(newURL, "hbb", "1");
        }
        if (quickBiddingError) {
            newURL = insertURLParameter(newURL, "qbeb", "1");
        }

        return newURL;
    }

    const updateBiddingPausedStatus = (biddingInputPaused) => {
        // Clearing the timeout so that we have a bit more control over the timing of the updates
        // Since the status just changed we may get fresh data from the server instead of the simple Update Information 
        clearTimeout(auctionInformationTimeout.current);
        // Patch in the update to the data
        updateLatestBiddingData(null, biddingInputPaused);
        // Reset the timeout to check again in 5 seconds, if necessary
        if (countdownListings.length > 0) {
            auctionInformationTimeout.current = setTimeout(fetchAuctionData, 5000);
        }
    }

    const updateLatestBiddingData = (auctionUpdateDataArray, biddingInputPaused) => {
        const auctionUpdateData = auctionUpdateDataArray?.length > 0 ? auctionUpdateDataArray[0] : null;
        const updatedData = patchInUpdatedAuctionData(latestAuctionBiddingData.current, auctionUpdateData, biddingInputPaused);

        if (updatedData.TimeRemaining > latestTimeRemaining.current) {
            setTimeExtended(true);
        }
        latestTimeRemaining.current = updatedData.TimeRemaining;

        if (updatedData.TimeRemaining <= 0 && window && !getURLParameter(window.location.href, "AuctionEnded")) {
            auctionHasEnded();
        }

        // Only consider the paused status as changed if it is turning back on
        const allBiddingPausedChanged = biddingInputPaused != null && latestAuctionBiddingData.current.IsAllBiddingPaused != biddingInputPaused && biddingInputPaused == false;
        const listingBiddingPausedChanged = latestAuctionBiddingData.current.IsListingPaused != updatedData.ListingIsPaused && updatedData.ListingIsPaused == false;

        setAuctionBiddingData(updatedData);
        latestAuctionBiddingData.current = updatedData;

        // User bid on another window, force fresh pull from server
        if (updatedData.BiddingUserID > 0 && updatedData.BiddingUserID == parseInt(auctionUpdateData.HighBidderLowerPubTUID) && updatedData.UserBidInfo && updatedData.UserBidInfo.UsersLastBid == 0) {
            resetAuctionData(null, null, null);
        }
        // Force a fresh pull from the server if either status was changed
        if (allBiddingPausedChanged || listingBiddingPausedChanged) {
            resetAuctionData(null, null, null);
        }

        if (waitingForQuickBidAttemptResponse.current) {
            setWaitingForQuickBidAttemptResponseFlag(false);
        }
    }

    useEffect(() => {
        if (countdownListings.length > 0) {
            // Make an AJAX call every 5 seconds
            auctionInformationTimeout.current = setTimeout(fetchAuctionData, 5000);
        }
        return () => clearTimeout(auctionInformationTimeout.current);
    }, [countdownListings]);

    const auctionListingNowHasUnder1HourLeft = (secondsRemaining) => {        
        setAuctionBiddingData(previousState => ({ ...previousState, TimeRemaining: secondsRemaining }));

        if (hasAuctionListingGroupListings) {
            //Need to show group extension message
            setshowGroupExtensionNote(true);
        }
    } 

    const auctionUpdatesStartPollingUpdate = (secondsRemaining, listingToAdd) => {
        setAuctionBiddingData(previousState => ({ ...previousState, TimeRemaining: secondsRemaining }));    
    } 

    const showBuyNowButton = () => {
        return props.BuyNowModel && props.BuyNowModel.BuyNowURL && !props.PricingInfo.ShowWholesalePriceOnly;
    }

    const toggleBuyersPremium = () => {
        setShowBuyersPremium(showBuyersPremium => !showBuyersPremium);
    }

    const showPayPalbutton = () => {
        return props.PayPalGateway &&
            props.PayPalGateway.PayPalAmount &&
            props.PayPalGateway.PublicKey &&
            props.PayPalGateway.PayPalCurrencyCode &&
            props.PayPalGateway.PayPalIntent &&
            props.PayPalGateway.LoginID &&
            props.PayPalGateway.ProductId > 0;
    }

    const hasBadges = () => {
        return props.Badges?.length > 0 || props.CarFaxInfo?.CarFaxURL;
    }

    function formatEventLocation() {
        const htmlString = props.AuctionDetails?.EventLocation;
        if (htmlString) {
            return htmlString.replace(/<br\s*\/?>/gi, ' ');
        }
        return null;
    }
    const unformattedEventLocation = formatEventLocation();

    const lotNumberSection = () => {
        return <>{
            props.LotNumber &&
            <div className='lot-section'>
                {props.AuctionDetails.prevLotListingID > 0 &&
                    <div className='prev-lot'><a href={'/listing/upcoming-auctions/' + props.AuctionDetails.prevLotListingID}><i className='fas fa-arrow-left' /> {translator.translate('sandhills.tradesites.mvc.prevlot')}</a></div>
                }
                <div className='current-lot'>Lot #{props.LotNumber}</div>
                {props.AuctionDetails.nextLotListingID > 0 &&
                    <div className='next-lot'><a href={'/listing/upcoming-auctions/' + props.AuctionDetails.nextLotListingID}>{translator.translate('sandhills.tradesites.mvc.nextlot')} <i className='fas fa-arrow-right' /></a></div>
                }
            </div>
        }</>
    }

    const getFormAssembly = (modalData) => {
        var m = modalData;
        sandhillsRequest({
            url: '/ajaxcontent/getformassembly/',
            method: 'post',
            data: {
                FirstName: m.FirstName,
                Lastname: m.Lastname,
                Email: m.Email,
                Phone: m.Phone,
                Country: m.Country,
                PostalCode: m.PostalCode,
                DealerContact: m.DealerContact,
                DealerLocation: m.DealerLocation,
                ListingURL: m.ListingURL,
                ListingID: m.ListingID,
                SerialNumber: m.SerialNumber,
                SiteID: currentSite.SiteID,
                ListingURL: m.ListingURL,
                LanguageID: currentUser.LanguageID,
            },
            errorHandling: {
                message: 'Error retrieving Send-A-Message form.',
                showDetails: false,
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            if (response) {
                setSamModel(previousState => {
                    const modalData = {
                        ...previousState,
                        FormAssembly: response
                    }

                    return modalData
                })
            }
        });
    }

    return (
        <div className='detail-wrapper'>
            {!props.UnauthorizedAuctionResultRequest &&
                <>
                    {(props.AuctionDetails?.BuyersPremiumData && showBuyersPremium) &&
                        <BuyersPremiumModal
                            {...props.AuctionDetails.BuyersPremiumData}
                            onClose={toggleBuyersPremium}
                        />}

                    <CategoryInformation {...props.CategoryInformation} />

                    {showEmployeeModal &&
                        <EmployeeListingModal
                            {...props.EmployeeListingViewModel}
                            listingTitle={props.ListingTitle}
                            inventoryManagementUrl={props.InventoryManagementUrl}
                            toggleEmployeeModal={toggleEmployeeModal}
                            listingId={props.ListingID}
                            isDetailPage
                        />}

                    {props.IsExpired &&
                        <div className='detail__expired-listing'>
                            <ExpiredListingWarning />
                        </div>}
                    {useBetaSiteCookie && showEmployeeOnlyBetaSiteToggle &&
                        <div className='detail__beta-site-warning'>
                            <BetaSiteCookieWarning />
                        </div>}
                </>}
            {props.BreadCrumbs &&
                <div className='detail__breadcrumbs'>
                    <ComplexBreadcrumb BreadCrumbModels={props.BreadCrumbs} />
                    <div style={{ justifyContent: 'space-between', display: 'flex' }}>
                        <div style={{ display: 'inline-block', alignItems: 'center' }}>
                            {!currentSite.IsMobileAppContext && props.EnableSearchResultsButton &&
                                <ThemedLinkButton variant='text' size='small' className='detail__search-results' onClick={() => history.back()}>
                                    <Icon baseClassName='fas' className='fa-angle-double-left' />
                                    <span className='detail__search-results-text'><TranslationText tKey='sandhills.mobileapps.searchresults' /></span>
                                </ThemedLinkButton>
                            }
                        </div>
                        {windowSize.isMobile &&
                            <OptionsPopper
                            IsDetailPage={true}
                            ShowCompare={props.ShowCompare}
                            UpdateCompareListing={updateCompareListing}
                            IsCheckedForCompare={compareIsChecked}
                            IsDisabled={props.IsCompareChkBoxDisabled}
                            CompareUrl={props.CompareUrl}
                            classNames={'detail__compare-listings'}
                            Id={props.ListingID}
                            ownerCRMID={props.OwnerCRMID}
                            externalID={props.ExternalID}
                            ShowCompareURL={props.ShowCompareURL}
                            baseCatID={props.CategoryInformation.BaseCatID}
                            />}
                    </div>
                    
                </div>}

            {windowSize.isMobile &&
                <div className='detail__heading'>
                    {lotNumberSection()}
                    <BaseTypography className='detail__title-container' component='div'>
                        <h1 className='detail__title'>{props.DisplayedListingTitle || props.ListingTitle}</h1>

                        {(props.ShowEmployeeOnlyInfo && !props.IsFreeListing) &&
                            <Button variant='text' size='small' className='detail__show-employee-only-specs' onClick={toggleEmployeeModal} title='Show employee only specs' sx={{ minWidth: 'min-content' }}>
                                <Icon baseClassName='fas' className='fa-external-link-alt' sx={{ marginLeft: '0' }} />
                            </Button>}
                    </BaseTypography>

                    {props.InventoryManagementUrl &&
                        <Link variant='text' size='small' href={props.InventoryManagementUrl} underline='hover' className='detail__im-link' target='_blank'>
                            View in Inventory Management <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                            <Icon baseClassName='fas' className='fa-external-link-alt' />
                        </Link>}
                    {props.UseSEOHTagReorganization ?
                        <h2 className='detail__category header-reset'>{props.CategoryInformation.DisplayCategoryName}</h2>
                        :
                        <div className='detail__category'>{props.CategoryInformation.DisplayCategoryName}</div>
                    }

                    {props.IsPreATResult && !props.AuctionDetails?.IsAuctionResult && currentSite?.Site?.IsHostedSite &&
                        <div className='detail__at-result'>
                            <img className='listing-prices__additional-image' src={props.PricingInfo.preATIcon} alt="" />
                            <span className='pre-at-result'>Pre-AuctionTime Result</span>
                        </div>
                    }
                    <div className='detail__heading-actions'>
                        {props.ShowWatchList &&
                            <Link variant='text' size='small' href={props.WatchlistUrl} underline='hover' className='detail__mobile-watchlist-link'>
                                {translator.translate('sandhills.tradesites.mvc.viewmywatchlist')}
                                <Icon baseClassName='fas' className='fa-external-link-alt' />
                            </Link>}
                        <div>
                            {props.ShowEmailThis &&
                                <MediaButton id='mobile-share' className='email-this des-email-this' ref={(element) => { returnFocusRef.current[inquiryType.EmailThis] = element }} onClick={() => { openThisEmailModal(inquiryType.EmailThis, '', returnFocusRef.current[inquiryType.EmailThis]); }} sx={{ padding: '10px', margin: 0, minWidth: 'auto', minHeight: 'auto', backgroundColor: 'transparent', boxShadow: 'none', fontSize: '1.125rem' }}>
                                    <Icon baseClassName='fas' className='fa-share-alt detail__media-buttons-icon' />
                                    <span className='visually-hidden'><TranslationText tKey='sandhillssystem.share' /></span>
                                </MediaButton>}
                            {props.ShowBidHistory &&
                                <MediaButton id='mobile-bid-history' onClick={() => openBidHistoryModal()} sx={{ padding: '10px', margin: 0, minWidth: 'auto', minHeight: 'auto', backgroundColor: 'transparent', boxShadow: 'none', fontSize: '1.125rem' }}>
                                    <Icon baseClassName='fas' className='fa-file detail__media-buttons-icon' />
                                    <span className='visually-hidden'><TranslationText tKey='sandhills.bidhistory' /></span>
                                </MediaButton>}
                            {props.ShowWatchList &&
                                <MediaButton className={'detail__watchlist-button ' + (isSavedToWatchlist ? ('watchlist-delete') : ('watchlist-add'))} data-listing-id={props.ListingID} data-action={isSavedToWatchlist ? 'delete' : 'add'} onClick={(e) => { watchlistClick(); toggleWatchlistExpansion(); }} sx={{ padding: '10px', margin: 0, minWidth: 'auto', minHeight: 'auto', backgroundColor: 'transparent', boxShadow: 'none', fontSize: '1.125rem' }}>
                                    <Icon baseClassName='fas' className='fa-heart detail__watchlist-icon' />
                                    <Icon baseClassName='far' className='fa-heart detail__watchlist-icon' />
                                    <span className='visually-hidden'>{isSavedToWatchlist ? translator.translate('sandhills.tradepubwebsite.mvc.sandhills.removefromwatchlistwithspace') : translator.translate('sandhills.tradepubwebsite.mvc.addtowatchlist')}</span>
                                </MediaButton>}
                        </div>
                    </div>
                    {displayWatchlistExpansion &&
                        <WatchlistExpansion {...props} Id={props.ListingID} isMobilePage={windowSize.isMobile} isDetailPage={true} toggleWatchlistExpansion={toggleWatchlistExpansion} UserWatchlist={watchlistChecked} forceExpansion={expansionFocus} />
                    }
                </div>}

            {!windowSize.isMobile &&
                <div className='detail__media-buttons media-buttons'>
                    {props.ShowPrintThis && !props.UnauthorizedAuctionResultRequest &&
                        <MediaButton id='detail-page-print-this' href={props.PrintUrl} target='_blank' rel='nofollow' className='print-this des-print-this' onClick={() => gtmPushEvent(gtmEventType.printListDetail)}>
                            <Icon baseClassName='fas' className='fa-print detail__media-buttons-icon' />
                            <TranslationText tKey='sandhills.print' />
                            <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                        </MediaButton>}
                    {props.ShowEmailThis &&
                        <MediaButton id='share' className='email-this des-email-this' ref={(element) => { returnFocusRef.current[inquiryType.EmailThis] = element }} onClick={() => { openThisEmailModal(inquiryType.EmailThis, '', returnFocusRef.current[inquiryType.EmailThis]); }}>
                            <Icon baseClassName='fas' className='fa-share-alt detail__media-buttons-icon' />
                            <TranslationText tKey='sandhillssystem.share' />
                        </MediaButton>}
                    {props.ShowBidHistory &&
                        <MediaButton id='bid-history' onClick={() => openBidHistoryModal()}>
                            <Icon baseClassName='fas' className='fa-file detail__media-buttons-icon' />
                            {props.AuctionDetails?.IsAuctionResult || currentSite.Site.IsAuctionTime ?
                                <TranslationText tKey='sandhills.bidhistory' />
                                :
                                <TranslationText tKey='sandhills.admindeleteprebids' />}
                        </MediaButton>}
                </div>}

            {props.IsGrayMarket &&
                <div className='detail__gray-market-disclaimer'>
                    <span>
                        <strong>
                            <i className='fas fa-exclamation-triangle gray-market-dislaimer-icon' aria-hidden='true' /> <TranslationText tKey='sandhills.tradesites.mvc.graymarketlisting' /> </strong>
                        -  <TranslationText tKey='sandhills.tradesites.mvc.graymarketdisclaimer' />
                    </span>
                </div>}

            <div className='detail__banner-ad'>
                {props.LoanApprovalEstimateModel?.ShowBanner && <LoanApprovalEstimateBanner {...props.LoanApprovalEstimateModel} pageType='detail' />}
            </div>

            <main id='main-content' className='detail__main-content'>
                {(auctionBiddingData?.UserBidInfo?.ListingBidStatusText || props.AuctionDetails?.IsLiveEvent) && !props.UnauthorizedAuctionResultRequest && !auctionBiddingData?.IsAllBiddingPaused && !auctionBiddingData?.IsListingPaused &&
                    <div className='detail__bidder-status'>
                        {auctionBiddingData?.UserBidInfo?.ListingBidStatusText && !auctionBiddingData.UserBidInfo.UserIsTieBidder &&
                            <div className={'bidder-status-text' + (auctionBiddingData.UserBidInfo.UserIsOutBid ? ' bidder-status_outbid' : ' bidder-status_high-bidder')} >
                                {auctionBiddingData.UserBidInfo.ListingBidStatusText} &nbsp;
                                {auctionBiddingData.UserBidInfo.UserIsWinningBidder && (
                                    <span onClick={() => openWinningBidderModal()} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                                         <TranslationText tKey='sandhills.seemoredetails' />
                                    </span>
                                )}
                            </div>

                        }
                        {auctionBiddingData?.UserBidInfo?.ListingBidStatusText && auctionBiddingData.UserBidInfo?.UserIsTieBidder &&
                            <div className={'bidder-status-text bidder-status_tie-bidder bidder-status_tie-bidder-text'} >
                                <div>
                                    <strong>{translator.translate('sandhills.tradepubwebsite.mvc.youarenothighbidder')}</strong>
                                </div>
                                <div>
                                    <br />
                                    <span dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.tradepubwebsite.mvc.tieshighbid') }}></span>
                                    <br />
                                    {translator.translate('sandhills.tradepubwebsite.mvc.tiebidpolicy')}
                                    <br />
                                    <span dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.tradepubwebsite.mvc.yournextbid').replace("{0}", `<strong>${auctionBiddingData?.NextBidAmount}</strong>`) }}></span>
                                </div>
                            </div>
                        }

                        {props.AuctionDetails?.IsLiveEvent && props.AuctionDetails?.IsEF &&
                            <div className='bidder-status-text bidder-status_live-auction'>{translator.translate('sandhills.tradepubwebsite.mvc.auctionislive')}</div>
                        }
                    </div>}
                {quickBiddingError && !auctionBiddingData?.IsAllBiddingPaused && !auctionBiddingData?.IsListingPaused &&
                    <div className='detail__bidder-status'>
                        <div className='bidder-status-text bidder-status_outbid'>{translator.translate('sandhills.tradepubwebsite.mvc.bidtoolow')}</div>
                    </div>}

                <div className='detail__left-col'>
                    <div className={imageCarouselClass}>
                        {props.MediaModel?.Media?.length > 0 ?
                            <MediaControl {...props.MediaModel} ShowMediaOverlayBanner={props.ShowMediaOverlayBanner} MediaOverlayBanner={props.MediaOverlayBanner} /> :
                            <div className='media-control-no-photo' />}
                        {props.FloorPlanUrlModel && <FloorPlan ShowThumb={true} {...props.FloorPlanUrlModel} />}
                    </div>

                    {hasBadges() && !props.UnauthorizedAuctionResultRequest &&
                        <div className='detail__badge-container'>
                            {(props.Badges && props.Badges.length > 0) &&
                                <BadgeList
                                    Badges={props.Badges}
                                    ref={returnFocusRef}
                                />}
                            {props.CarFaxInfo?.CarFaxURL &&
                                <div className='carfax'>
                                    <a id='detail-page-carfax-link' href={props.CarFaxInfo.CarFaxURL} target='_blank'><img className='carfax__image' src={props.CarFaxInfo.IconURL} alt='CarFax' /> <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')}</span> </a>
                                </div>}
                        </div>}
                </div>

                {!props.UnauthorizedAuctionResultRequest && !currentSite.RequiresFullRegistration || (currentSite.RequiresFullRegistration && !currentUser.IsMinReg) ?
                    <>
                        <div className='detail__right-col'>
                            <div className='detail__details'>
                                {!windowSize.isMobile &&
                                    <div className='detail__heading'>
                                        {lotNumberSection()}
                                        <div className='detail__title-container'>
                                            <h1 className='detail__title'>{props.DisplayedListingTitle || props.ListingTitle}</h1>

                                            {props.ShowEmployeeOnlyInfo && !props.IsFreeListing &&
                                                <Button variant='text' size='small' className='detail__show-employee-only-specs' onClick={toggleEmployeeModal} title='Show employee only specs' sx={{ minWidth: 'min-content' }}>
                                                    <Icon baseClassName='fas' className='fa-external-link-alt' sx={{ marginLeft: '0' }} />
                                                </Button>}
                                            <OptionsPopper
                                                IsDetailPage={true}
                                                ShowCompare={props.ShowCompare}
                                                UpdateCompareListing={updateCompareListing}
                                                IsCheckedForCompare={compareIsChecked}
                                                IsDisabled={props.IsCompareChkBoxDisabled}
                                                CompareUrl={props.CompareUrl}
                                                classNames={'detail__compare-listings'}
                                                Id={props.ListingID}
                                                ownerCRMID={props.OwnerCRMID}
                                                externalID={props.ExternalID}
                                                ShowCompareURL={props.ShowCompareURL}
                                                baseCatID={props.CategoryInformation.BaseCatID}
                                            />
                                        </div>

                                        {props.InventoryManagementUrl &&
                                            <Link variant='text' size='small' href={props.InventoryManagementUrl} underline='hover' className='detail__im-link' target='_blank'>
                                                View in Inventory Management <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                                                <Icon baseClassName='fas' className='fa-external-link-alt' />
                                            </Link>}
                                        {props.UseSEOHTagReorganization ?
                                            <h2 className='detail__category header-reset'>{props.CategoryInformation.DisplayCategoryName}</h2>
                                            :
                                            <div className='detail__category'>{props.CategoryInformation.DisplayCategoryName}</div>
                                        }

                                        {props.IsPreATResult && !props.AuctionDetails?.IsAuctionResult && currentSite?.Site?.IsHostedSite &&
                                            <div className='detail__at-result'>
                                                <img className='listing-prices__additional-image' src={props.PricingInfo.preATIcon} alt="" />
                                                <span className='pre-at-result'>Pre-AuctionTime Result</span>
                                            </div>
                                        }
                                        {props.ShowWatchList &&
                                            <div className='detail__watchlist-container'>
                                                <IconButton title={isSavedToWatchlist ? ('Delete from watchlist') : ('Add to watchlist')} variant='text' size='small' className={'detail__watchlist-button ' + (isSavedToWatchlist ? ('watchlist-delete') : ('watchlist-add'))} data-listing-id={props.ListingID} data-action={isSavedToWatchlist ? 'delete' : 'add'} onClick={(e) => { watchlistClick(); toggleWatchlistExpansion(); }} sx={{ color: 'inherit', padding: '0px 15px 0px 5px' }}>
                                                    <Icon baseClassName='fas' className='fa-heart detail__watchlist-icon' />
                                                    <Icon baseClassName='far' className='fa-heart detail__watchlist-icon' />
                                                </IconButton>
                                                <Link variant='text' size='small' href={props.WatchlistUrl} underline='hover'>
                                                    {translator.translate('sandhills.tradesites.mvc.viewmywatchlist')}
                                                    <Icon baseClassName='fas' className='fa-external-link-alt' />
                                                </Link>
                                                {displayWatchlistExpansion &&
                                                    <WatchlistExpansion {...props} isMobilePage={windowSize.isMobile} isDetailPage={true} Id={props.ListingID} toggleWatchlistExpansion={toggleWatchlistExpansion} UserWatchlist={watchlistChecked} forceExpansion={expansionFocus} />
                                                }
                                            </div>
                                        }
                                    </div>
                                }

                                {!props.IsDismantled && <MainDetailPricing {...props.PricingInfo} IsRY={isRY} auctionBiddingData={auctionBiddingData} auctionDetails={props.AuctionDetails} variant={variant} timeRemaining={auctionBiddingData?.TimeRemaining}
                                    setCountdownListings={setCountdownListings} ListingID={props.ListingID} BaseCategoryId={props.BaseCatID} endTime={Date.now() + (auctionBiddingData?.TimeRemaining * 1000)} timeExtended={timeExtended}
                                    isPrintPage={props.IsPrintPage} auctionListingNowHasUnder1HourLeft={auctionListingNowHasUnder1HourLeft} auctionUpdatesStartPollingUpdate={auctionUpdatesStartPollingUpdate} />
                                }
                                {props.AuctionDetails?.IsAuctionResult &&
                                    <>
                                        {props.AuctionDetails?.IsAT &&
                                            <>
                                                {props.AuctionDetails.BuyersPremiumData?.BuyersPremiumFee &&
                                                    <div className='detail__buyers-premium'>
                                                        <strong><TranslationText tKey='sandhills.buyerspremiumincludedinprice' />:&nbsp;</strong>
                                                        {props.AuctionDetails.BuyersPremiumData.BuyersPremiumFee}
                                                    </div>
                                                }
                                                {auctionBiddingData?.NumberOfBids > 0 &&
                                                    <div className='detail__bid-count'>
                                                        <strong><TranslationText tKey='sandhills.tradepubwebsite.mvc.numberofbids' />:&nbsp;</strong>
                                                        {auctionBiddingData.NumberOfBids}
                                                    </div>
                                                }
                                                {auctionBiddingData?.StartingBidFormatted &&
                                                    <div className='detail__opened-at'>
                                                        <strong><TranslationText tKey='sandhills.tradepubwebsite.mvc.openedat' />:&nbsp;</strong>
                                                        {auctionBiddingData.StartingBidFormatted}
                                                    </div>
                                                }
                                            </>
                                        }
                                        
                                        {props.AuctionDetails?.AuctionDate &&
                                            <div className='detail__auction-date'>
                                                <strong>{props.AuctionDetails.AuctionDateLabel}:&nbsp;</strong>
                                                {props.AuctionDetails.AuctionDate}
                                            </div>}                                                                                                              
                                    </>
                                }
                                <div className="calc-button-wrapper">
                                    {props.Widgets?.ShowFinancialCalculator &&
                                        <CalculatorCurrencyBtn OpenCalculator={toggleCalculator} {...props} ref={returnFocusRef} />
                                    }
                                    {props.Widgets.ShowLenderWidget && props.Widgets.PaymentsAsLowAs != '' &&
                                        <PaymentsAsLowAsLink
                                            {...props.Widgets}
                                            Manufacturer={props.Manufacturer}
                                            Model={props.Model}
                                            ListingType={props.ListingType}
                                            ListingID={props.ListingID}
                                            Condition={props.Condition}
                                            CategoryId={props.CategoryInformation?.CategoryID}
                                            AltCategoryId={props.CategoryInformation?.AltCategoryID}
                                            pageType='detail'
                                        />}
                                </div> 
                                {props.AuctionDetails?.BuyersPremiumData &&
                                    <Button variant='text' size='small' className='bidding-box-details__view-buyers-premium' onClick={toggleBuyersPremium} sx={{ justifyContent: 'flex-start' }}>
                                        <TranslationText tKey='Sandhills.ViewBuyersPremium' />
                                        <Icon baseClassName='fas' className='fa-external-link-alt' />
                                    </Button>
                                }
                                {hasVariants && renderVariantOptions()}
                                {outOfStock && <div className="product-out-of-stock">{translator.translate('sandhills.outofstock')}</div>}
                                {props.SNGuideRoute &&
                                    <div className='sn-guide-link'>
                                        <a href={props.SNGuideRoute} className='sn-route' target='_blank'>
                                            <TranslationText tKey='sandhills.tradesites.mvc.viewhistoricalrecords' /><i className='fas fa-external-link-alt' />
                                        </a>
                                    </div>
                                }
                                {isRY && showBuyNowButton() &&
                                    <ThemedButton
                                        variant='contained'
                                        size='small'
                                        href={buyNowURL}
                                        target={(props.BuyNowModel.BuyNowNewTab || currentSite.IsMobileAppContext) ? '_blank' : '_self'}
                                        className={'gtm-buynow des-buy-now rent-now'}
                                        id='detail-page-buynow'
                                        style={{ width: '100%', }}
                                        onClick={handleBuyNowClick} sx={{ flexBasis: '48%' }}>
                                        <Icon baseClassName='fas' className='fa-solid fa-key' sx={{ pr: '5px' }} />
                                        {props.BuyNowModel.BuyNowButtonText}
                                        {(props.BuyNowModel.BuyNowNewTab || currentSite.IsMobileAppContext) && <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>}
                                    </ThemedButton>
                                }
                                <div className='detail__cta-buttons' ref={returnFocusRef}>
                                    {!outOfStock && (showBuyNowButton() || showPayPalbutton()) &&
                                        <>
                                            {showBuyNowButton && !isRY &&
                                                <ThemedButton
                                                    variant='contained'
                                                    size='small'
                                                    href={buyNowURL}
                                                    target={(props.BuyNowModel.BuyNowNewTab || currentSite.IsMobileAppContext) ? '_blank' : '_self'}
                                                    className={'gtm-buynow des-buy-now'}
                                                    id='detail-page-buynow'
                                                    onClick={handleBuyNowClick} sx={{ flexBasis: '48%' }}>
                                                    <Icon baseClassName='fas' className='fa-shopping-cart detail__purchase-options-icon gtm-noclick' />
                                                    {props.BuyNowModel.BuyNowButtonText}
                                                    {(props.BuyNowModel.BuyNowNewTab || currentSite.IsMobileAppContext) && <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>}
                                                </ThemedButton>}
                                            {showPayPalbutton() &&
                                                <div className='paypal-button-container'>
                                                    <PayPalButton
                                                        options={{
                                                            clientId: props.PayPalGateway.PublicKey,
                                                            merchantId: props.PayPalGateway.LoginID,
                                                            intent: props.PayPalGateway.PayPalIntent.toLowerCase(),
                                                            commit: false,
                                                            components: 'buttons',
                                                            currency: props.PayPalGateway.PayPalCurrencyCode,
                                                        }}
                                                        locale={'en-US'}
                                                        productId={props.PayPalGateway.ProductId}
                                                        quantity={1}
                                                        style={{
                                                            size: 'responsive',
                                                            layout: 'horizontal',
                                                            color: 'gold',
                                                            shape: 'pill',
                                                            label: 'checkout',
                                                            height: 45,
                                                            tagline: false
                                                        }}
                                                        loadSdk
                                                        onButtonReady={() => setPaypalSdkLoaded(true)}
                                                    />
                                                </div>}
                                        </>
                                    }

                                    {props.ContactInfo?.ShowSendAMessage && !props.AuctionDetails &&
                                        <>
                                            {props.IsPreTrade &&
                                                <ThemedButton
                                                    color={currentSite.ThemeOverride}
                                                    variant='contained'
                                                    size='small'
                                                    className={'dealer-contact__button mao'}
                                                    id='detail-page-email-offer'
                                                    ref={(element) => { returnFocusRef.current[inquiryType.Email + '-offer'] = element }}
                                                    onClick={() => openThisEmailModal(inquiryType.Offer, '', returnFocusRef.current[inquiryType.Email + '-offer'], false, false, true)}
                                                    sx={{ flexBasis: '48%' }}>
                                                    <TranslationText tKey='sandhills.makeanoffer' />
                                                </ThemedButton>
                                            }
                                            <ThemedButton
                                                color={currentSite.ThemeOverride}
                                                variant='contained'
                                                size='small'
                                                className={'dealer-contact__button'}
                                                id='detail-page-email-seller'
                                                ref={(element) => { returnFocusRef.current[inquiryType.Email + '-retail'] = element }}
                                                onClick={() => openThisEmailModal(inquiryType.Email, '', returnFocusRef.current[inquiryType.Email + '-retail'])}
                                                sx={{ flexBasis: '48%' }}>
                                                <Icon baseClassName='fas' className='fa-envelope dealer-contact__icon' />
                                                {props.CustomEmailSellerTextForRental ? props.CustomEmailSellerTextForRental : props.CustomEmailSellerTextForRetail ? props.CustomEmailSellerTextForRetail : props.IsForRentOnly || showEmailTextOnly ? <TranslationText tKey='sandhills.email' /> : <TranslationText tKey='sandhills.emailseller' />}
                                            </ThemedButton>

                                            {props.ContactInfo.ShowCheckAvailability &&
                                                <CheckAvailabilityButton
                                                    color={currentSite.ThemeOverride}
                                                    variant='contained'
                                                    size='small'
                                                    className={'dealer-contact__button'}
                                                    id='detail-page-check-availibility'
                                                    ref={(element) => { returnFocusRef.current[inquiryType.CheckAvailability] = element }}
                                                    onClick={() => openThisEmailModal(inquiryType.CheckAvailability, '', returnFocusRef.current[inquiryType.CheckAvailability])}
                                                    sx={{ flexBasis: '48%' }}>
                                                    <Icon baseClassName='fas' className='fa-check dealer-contact__icon' />
                                                    <TranslationText tKey='sandhills.checkavailability' />
                                                </CheckAvailabilityButton>}
                                        </>}
                                </div>

                                {isSavedToWatchlist &&
                                    <WatchListComment
                                        key={`watchlistcomment-${props.ListingID}`}
                                        ListingID={props.ListingID}
                                        Comment={watchListComment}
                                        IsDetailsPage={true} />}

                                {(props.ContactInfo?.ListingLocation) &&
                                    <div className='detail__machine-location'>
                                        {props.AuctionDetails?.EventLocation && currentSite?.Site?.IsBidcaller &&
                                            <>
                                                <ConditionalWrapper condition={props.UseSEOHTagReorganization} wrapper={children => <h2 className="header-reset">{children}</h2>}>
                                                    <strong className='detail__machine-location-label-text'>{translator.translate('sandhills.tradepubwebsite.mvc.eventlocation')}:</strong>
                                                </ConditionalWrapper>
                                                <ThemedLink variant='text' size='small' href={props.AuctionDetails?.EventLocationMapLink ? props.AuctionDetails?.EventLocationMapLink : ''} underline='hover' className={props.AuctionDetails?.EventLocationMapLink ? '' : 'anchor-no-link'} target='_blank' rel='noopener'>
                                                    <>
                                                        <>
                                                            <span>{unformattedEventLocation}</span>
                                                        </>
                                                        {props.AuctionDetails?.EventLocationMapLink &&
                                                            <>
                                                                <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                                                                <Icon baseClassName='fas' className='fa-external-link-alt' />
                                                            </>}
                                                    </>
                                                </ThemedLink>
                                                <br></br>
                                            </>
                                        }
                                        <ConditionalWrapper condition={props.UseSEOHTagReorganization} wrapper={children => <h2 className="header-reset">{children}</h2>}>
                                            <strong className='detail__machine-location-label-text'>{props.ContactInfo.ListingLocation.DisplayText}</strong>
                                        </ConditionalWrapper>
                                        <ThemedLink variant='text' size='small' href={props.ContactInfo?.ListingLocation?.GoogleMapsUrl ? props.ContactInfo.ListingLocation.GoogleMapsUrl : ''} underline='hover' className={props.ContactInfo?.ListingLocation?.GoogleMapsUrl ? '' : 'anchor-no-link'} target='_blank' rel='noopener'>
                                            <>
                                                {props.ContactInfo.ListingLocation.ShowStreetAddress &&
                                                    <span className='detail__machine-location-address'>
                                                        {props.ContactInfo.ListingLocation.FullAddress}
                                                    </span>}
                                                <>
                                                    {props.ContactInfo.ListingLocation.FormattedLocation}
                                                </>
                                                {props.ContactInfo?.ListingLocation?.GoogleMapsUrl && props.ContactInfo?.ListingLocation?.FullAddress &&
                                                    <>
                                                        <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                                                        <Icon baseClassName='fas' className='fa-external-link-alt' />
                                                    </>}
                                            </>
                                        </ThemedLink>
                                    </div>}
                            </div>

                            <div className='detail__contact-info'>
                                {props.AuctionDetails?.AuctionListingGroupListings?.length > 0 &&
                                    <AuctionListingGroup
                                    {...props.AuctionDetails}
                                        showGroupExtensionNote={showGroupExtensionNote}
                                    />}

                                {(props.AuctionDetails?.IsAuctionListing && ((props.AuctionDetails?.IsAT && auctionBiddingData?.BiddingAllowed) || (props.AuctionDetails?.IsEF && props.AuctionDetails?.BiddingAgreement))) &&
                                    <DetailBiddingBox
                                        {...props.AuctionDetails}
                                        quickBidEnabled={quickBiddingEnabled}
                                        resetAuctionData={resetAuctionData}
                                        setQuickBidValues={setQuickBidValues}                                        
                                        setWaitingForQuickBidAttemptResponseFlag={setWaitingForQuickBidAttemptResponseFlag}
                                        AuctionBiddingData={auctionBiddingData}
                                        toggleBuyersPremium={toggleBuyersPremium}
                                        notificationSettings={props.notificationSettings}
                                        WatchlistUrl={props.WatchlistUrl}
                                        listingCountry={props.ContactInfo?.ListingLocation?.Country}
                                        BidPopUpURL={props.BidPopUpURL}
                                        ContactInfo={props.ContactInfo}
                                        AuctionID={props.StandaloneSaleID || props.WeeklyAuctionGroupID}
                                        AuctionName={props.AuctionGroupName}
                                    />}
                                {isRY && props.ShowRequestAQuote &&
                                    <DetailRequestAQuoteBox
                                        {...props}
                                        {...props.RequestAQuote}
                                        Industry={props.RequestAQuote.ValidBaseCategoriesForIndustry.filter(baseCatID => baseCatID == props.CategoryInformation.BaseCatID).length > 0 ? props.CategoryInformation.BaseCatID : props.CategoryInformation.LegacyBaseCatID}
                                        Category={props.CategoryInformation.CategoryID}
                                        ListingID={props.ListingID}
                                        ListingTitle={props.ListingTitle}
                                    />
                                }
                                {props.RequestACharter &&
                                    <DetailRequestACharterBox
                                        {...props}
                                        {...props.RequestACharter}
                                    />
                                }
                                {props.RentalContactInfo && isRY &&
                                    <>
                                        <DealerInfo
                                            {...props.RentalContactInfo}
                                            openEmailModal={openThisEmailModal}
                                            contactType={contactType.Rental}
                                            manufacturer={props.Manufacturer}
                                            model={props.Model}
                                            AltCategoryID={props.CategoryInformation.AltCategoryID}
                                            categoryID={props.CategoryInformation.CategoryID}
                                            listingType={props.ListingType}
                                            listingID={props.ListingID}
                                            condition={props.Condition}
                                            CustomEmailSellerTextForRental={props.CustomEmailSellerTextForRental}
                                            CustomEmailSellerTextForRetail={props.CustomEmailSellerTextForRetail}
                                            isAuction={props.AuctionDetails ? true : false}
                                            windowSize={windowSize}
                                            ref={returnFocusRef}
                                        />
                                    </>}

                                {(props.FleetEvaluatorModel) &&
                                    <ThemeProvider theme={(!theme.isHostedSite && theme.mainMode == "dark") ? theme.darkTheme : theme.lightTheme}>
                                        <ThemedBox>
                                            <FleetEvaluatorValues
                                                FEAuctionValue={props.FleetEvaluatorModel.FEAuctionValue}
                                                FERetailValue={props.FleetEvaluatorModel.FERetailValue}
                                                FEImagePath={props.FleetEvaluatorModel.FEImagePath}
                                                IsDetailsPage={true}
                                            />
                                        </ThemedBox>
                                    </ThemeProvider>}

                                {(props.WholesaleContactInfo && props.PricingInfo?.IsExclusiveWholeSale) &&
                                    <>
                                        <DealerInfo
                                            {...props.WholesaleContactInfo}
                                            openEmailModal={openThisEmailModal}
                                            contactType={contactType.ExclusiveWholesale}
                                            showSAMWOnLoad={props.SAMModel?.ShowSAMWOnLoad}
                                            manufacturer={props.Manufacturer}
                                            model={props.Model}
                                            AltCategoryID={props.CategoryInformation.AltCategoryID}
                                            categoryID={props.CategoryInformation.CategoryID}
                                            listingType={props.ListingType}
                                            listingID={props.ListingID}
                                            condition={props.Condition}
                                            CustomEmailSellerTextForRental={props.CustomEmailSellerTextForRental}
                                            CustomEmailSellerTextForRetail={props.CustomEmailSellerTextForRetail}
                                            isAuction={props.AuctionDetails ? true : false}
                                            windowSize={windowSize}
                                            ref={returnFocusRef}
                                        />
                                    </>}

                                {(props.ContactInfo && !props.ShowWholesaleContactInfoOnly && !props.IsForRentOnly) &&
                                    <>
                                        <DealerInfo
                                            {...props.ContactInfo}
                                            openEmailModal={openThisEmailModal}
                                            contactType={contactType.Retail}
                                            manufacturer={props.Manufacturer}
                                            model={props.Model}
                                            AltCategoryID={props.CategoryInformation.AltCategoryID}
                                            categoryID={props.CategoryInformation.CategoryID}
                                            listingType={props.ListingType}
                                            listingID={props.ListingID}
                                            condition={props.Condition}
                                            CustomEmailSellerTextForRental={props.CustomEmailSellerTextForRental}
                                            CustomEmailSellerTextForRetail={props.CustomEmailSellerTextForRetail}
                                            isAuction={props.AuctionDetails ? true : false}
                                            windowSize={windowSize}
                                            ref={returnFocusRef}
                                            viewAuctioneerLink={props.AuctionDetails?.ViewAuctioneerInfoLink}
                                        />
                                    </>}

                                {(props.WholesaleContactInfo && !props.PricingInfo.IsExclusiveWholeSale) && props.ShowWholesaleOnSite &&
                                    <>
                                        <DealerInfo
                                            {...props.WholesaleContactInfo}
                                            openEmailModal={openThisEmailModal}
                                            showSAMWOnLoad={props.SAMModel?.ShowSAMWOnLoad}
                                            contactType={contactType.Wholesale}
                                            isAuction={props.AuctionDetails ? true : false}
                                            windowSize={windowSize}
                                            ref={returnFocusRef}
                                        />
                                    </>}

                                {props.RentalContactInfo && !isRY &&
                                    <>
                                        <DealerInfo
                                            {...props.RentalContactInfo}
                                            openEmailModal={openThisEmailModal}
                                            contactType={contactType.Rental}
                                            manufacturer={props.Manufacturer}
                                            model={props.Model}
                                            AltCategoryID={props.CategoryInformation.AltCategoryID}
                                            categoryID={props.CategoryInformation.CategoryID}
                                            listingType={props.ListingType}
                                            listingID={props.ListingID}
                                            condition={props.Condition}
                                            CustomEmailSellerTextForRental={props.CustomEmailSellerTextForRental}
                                            CustomEmailSellerTextForRetail={props.CustomEmailSellerTextForRetail}
                                            isAuction={props.AuctionDetails ? true : false}
                                            windowSize={windowSize}
                                            ref={returnFocusRef}
                                        />
                                    </>}
                            </div>

                            {(props.BuyNowModel?.BuyNowURL && props?.FulfillmentOptions?.length > 0) &&
                                <ShippingInfo {...props.ShippingInfo} IsFreeShipping={props.IsFreeShipping} />}

                            <div className='detail__widgets'>
                                {(props.Widgets && !props.Widgets.HideAllWidgets) &&
                                    <WidgetList
                                    {...props.Widgets}
                                    Manufacturer={props.Manufacturer}
                                    Model={props.Model}
                                    ListingType={props.ListingType}
                                    ListingID={props.ListingID}
                                    Condition={props.Condition}
                                    CategoryId={props.CategoryInformation?.CategoryID}
                                    AltCategoryId={props.CategoryInformation?.AltCategoryID}
                                    pageType='detail'
                                    shippingEstimateData={shippingEstimateData}
                                    shippingEstimateImageUrl={props.ShippingEstimateImageUrl}
                                    />}
                            </div>

                        </div>

                        <div className='detail__specs'>
                            {props.ShowroomListingDetailHtml ?
                                <>
                                    {props.DescriptionSpec &&
                                        <>
                                            <h3 className='detail__specs-heading'><TranslationText tKey='sandhills.groupname.general' /></h3>
                                            <div className='detail__specs-wrapper'>
                                                <div className='detail__specs-label'>
                                                    {props.DescriptionSpec.LabelDisplayText}
                                                </div>
                                                <div className='detail__specs-value'>
                                                    {props.DescriptionSpec.Value}
                                                </div>
                                            </div>
                                        </>}

                                    <div className='showroomlisting-detailhtml' dangerouslySetInnerHTML={{ __html: props.ShowroomListingDetailHtml }} />
                                </>
                                :
                                <>
                                    {generalSpecs &&
                                        <>
                                            <h3 className='detail__specs-heading'><TranslationText tKey='sandhills.groupname.general' /></h3>
                                            <div className='detail__specs-wrapper'>
                                                {Object.keys(generalSpecs).map((key, index) =>
                                                    <Fragment key={key}>
                                                        <div className='detail__specs-label'>
                                                            {generalSpecs[key].LabelDisplayText}
                                                        </div>
                                                        <div className='detail__specs-value'>
                                                            {generalSpecs[key].Value}
                                                        </div>
                                                    </Fragment>
                                                )}
                                            </div>
                                        </>}
                                    {(categorySpecs?.length > 0) && categorySpecs.map(specGroup =>
                                        <>
                                            {specGroup.Specs &&
                                                <>
                                                    <h3 className='detail__specs-heading'>{specGroup.GroupName}</h3>
                                                    <div className='detail__specs-wrapper' key={specGroup.SpecGroupID}>
                                                        {Object.keys(specGroup.Specs).map((key, index) =>
                                                            <Fragment key={key}>
                                                                <div className='detail__specs-label'>
                                                                    {specGroup.Specs[key].LabelDisplayText}
                                                                </div>
                                                                <div className='detail__specs-value'>
                                                                    {specGroup.Specs[key].Value}
                                                                </div>
                                                            </Fragment>
                                                        )}
                                                    </div>
                                                </>}
                                        </>)}

                                    {props.TireTreadSpec?.TreadDepths?.length > 0 &&
                                        <TireTreadSpecs  {...props.TireTreadSpec} />}
                                </>}

                            {props.InspectionVM?.ShowInspections &&
                                <InspectionList Inspections={props.InspectionVM.Inspections} LangID={props.SiteLangID} />}

                            {props.Brochure &&
                                <Brochure Brochure={props.Brochure} />}

                            {props.ServiceLogs?.length > 0 &&
                                <ServiceLogs ServiceLogs={props.ServiceLogs} />}

                            {props.AdditionalInfoDocs?.length > 0 &&
                                <AdditionalInformation AddInfo={props.AdditionalInfoDocs} />}

                            {props.ShowACCReport &&
                                <StaticACCReport ACCUrl={props.ACCUrl} />}
                        </div>

                        {termsAndConditions &&
                            <BaseTypography className='detail__legal-notices' sx={{ color: 'inherit' }} component='div'>
                                <span className='detail__terms-conditions'>
                                    <h4 className='detail__legal-notices-heading'>{props.TermsAndConditionsText}</h4>
                                    <span className='detail__terms-conditions-text' dangerouslySetInnerHTML={{ __html: termsAndConditions }}></span>
                                    {props.AuctionDetails && isTermsAndConditionsMultiline &&
                                        <ThemedLinkButton variant='text' size='small' className='detail__terms-conditions-toggle' onClick={toggleTermsAndConditions}>
                                            {termsAndConditions.length < props.TermsAndConditions.length ? translator.translate('Sandhills.ContinueReading') : translator.translate('Sandhills.ShowLess')}
                                        </ThemedLinkButton>}
                                </span>
                            </BaseTypography>
                        }
                        <div className='detail__additional-listings'>
                            {(props.ContactInfo && props.ContactInfo.CompanyName && props.ContactInfo.SpecificCategoryListingsForContactURL && props.ContactInfo.AllListingsForContactURL) &&
                                <>
                                    <ThemedButton color={currentSite.ThemeOverride} variant='contained' size='small' href={props.ContactInfo.SpecificCategoryListingsForContactURL} id='detail-page-additional-listings-all-categories' className='additional-listings-link additional-listings-link_all-categoriesn template-branded-button' onClick={() => gtmPushEvent(gtmEventType.sellerAllCategoryClick)}>
                                        {props.SeeAllSellerDisplayCategory}
                                    </ThemedButton>
                                    <ThemedButton color={currentSite.ThemeOverride} variant='contained' size='small' href={props.ContactInfo.AllListingsForContactURL} id='detail-page-additional-listings-specific-category' className='additional-listings-link additional-listings-link_category-specific template-branded-button' onClick={() => gtmPushEvent(gtmEventType.sellerAllListingsClick)}>
                                        {props.SeeAllSellerListingsCategoryName}
                                    </ThemedButton>

                                    {currentSite?.Site?.IsAuctionTime &&
                                        <>
                                            {props.ContactInfo.IsBilledUser &&

                                                <ThemedButton color={currentSite.ThemeOverride} variant='contained' size='small' href={props.ContactInfo.AuctioneerConsignmentLink} id='detail-page-auctioneer-consignment' className='additional-listings-link additional-listings-link_auctioneer-consignment template-branded-button' onClick={() => gtmPushEvent(gtmEventType.auctioneerConsignmentButtonClick)}>
                                                    {props.ContactInfo.SellWithUsLabel}
                                                </ThemedButton>

                                            }

                                            {props.ConsignmentPackageID &&

                                                <ThemedButton color={currentSite.ThemeOverride} variant='contained' size='small' href={props.ConsignmentPackageLink} id='detail-page-auctioneer-consignment-list-page' className='additional-listings-link additional-listings-link_auctioneer-consignment template-branded-button' onClick={() => gtmPushEvent(gtmEventType.viewConsignmentPackageButtonClick)}>
                                                    {translator.translate('sandhills.viewconsignmentpackage')}
                                                </ThemedButton>


                                            }
                                            {props.StandaloneSaleID &&

                                                <ThemedButton color={currentSite.ThemeOverride} variant='contained' size='small' href={props.StandaloneSaleLink} id='detail-page-auctioneer-standalone-list-page' className='additional-listings-link additional-listings-link_auctioneer-standalone template-branded-button' onClick={() => gtmPushEvent(gtmEventType.viewStandaloneSaleButtonClick)}>
                                                    {translator.translate('sandhills.auctioncatalog')}
                                                </ThemedButton>

                                            }
                                        </>
                                    }
                                </>
                            }
                        </div>

                        {fitsTo?.length > 0 &&
                            <div className='detail__fits-to'>
                                <h3 className='fits-to-heading'><TranslationText tKey='sandhills.tradepubwebsite.mvc.attachmentfitsto' /></h3>
                                <ul className='fits-to-list'>
                                    {fitsTo.map(item =>
                                        <Fragment key={item.Key}>
                                            {item.Value && item.Value.length == 0 ?
                                                <li className='fits-to-list-item fits-to-list-item-manufacturer'>
                                                    <span className='fits-to-list-item-text'>{item.Key}</span>
                                                </li>
                                                :
                                                <li className='fits-to-list-item fits-to-list-item_has-children fits-to-list-item-manufacturer'>
                                                    <button className='fits-to-list-item-button' aria-expanded={(item.Expanded ? 'true' : 'false')} onClick={() => expandFitsTo(item)}>
                                                        <span className='fits-to-list-item-text'>{item.Key}</span>
                                                        <i className={'fas' + (item.Expanded ? ' fa-minus' : ' fa-plus')} />
                                                    </button>
                                                    {item.Expanded &&
                                                        <ul className='fits-to-list'>
                                                            {item.Value && item.Value.length > 0 &&
                                                                item.Value.map((model, i) =>
                                                                    <Fragment key={model}>
                                                                        <li className='fits-to-list-item fits-to-list-item-model'>{model}</li>
                                                                    </Fragment>)}
                                                        </ul>}
                                                </li>}
                                        </Fragment>)}
                                </ul>
                            </div>}

                        {similarListings && similarListings.length > 0 &&
                            <div className='similar-listings-section'>
                                <strong className='title'>{(currentSite.Site.IsBidcaller || currentSite.Site.IsAuctionTime) ? translator.translate('sandhills.viewsimilarauctions') : translator.translate('sandhills.recommendedforyou')}</strong>
                                <div className='thumb-container'>
                                    {similarListings.map((listing) =>
                                        <a href={listing.DestinationUrl} className='thumb advanced-photo-thumb'>
                                            <ListingImage IsEF={listing.IsEF} LinkTarget={listing.LinkTarget} IsAT={listing.IsAT} ShowMediaOverlayBanner={true}
                                                LazyLoad ListingTitle={listing.Title} AbsolutePathBaseUrl={listing.AbsolutePathBaseUrl} ShowAuctionBranding={true}
                                                MediaOverlayBanner={props.MediaOverlayBanner} IsDetailRecommendedListing={true}
                                                {...listing.ListingImageModel} />
                                            <div className='thumb-details'>
                                                {listing.Title && <div className='title'>{listing.Title}</div>}
                                                {listing.Description && <div className='desc'>{listing.Description}</div>}
                                            </div>
                                        </a>
                                    )}
                                </div>
                            </div>
                        }

                        <BaseTypography className='detail__legal-notices' sx={{ color: 'inherit' }} component='div'>
                            {props.ReturnPolicy &&
                                <span className='detail__return-policy'>
                                    <h4 className='detail__legal-notices-heading'>{props.ReturnPolicyText}</h4>
                                    <span className='detail__return-policy-text'>
                                        {props.ReturnPolicy}
                                    </span>
                                </span>}

                            {props.AuctionDetails?.BuyersPremiumData &&
                                <span className='detail__buyers-premium'>
                                    <h4 className='detail__legal-notices-heading'>{translator.translate('sandhills.buyerspremium')}</h4>
                                    <span className='detail__buyers-premium-form'>
                                        <BuyersPremiumForm {...props.AuctionDetails.BuyersPremiumData} />
                                    </span>
                                </span>}

                            {((props.ShowCurrencyDisclaimer && props.Widgets?.ShowLenderWidget && props.Widgets?.LenderCompanyName === 'Currency') || props.LoanApprovalEstimateModel?.ShowBanner) &&
                                <LoanApprovalEstimateDisclaimer Disclaimer={props.LoanApprovalEstimateModel.Disclaimer} />}

                            {props.Widgets?.LenderCompanyName === 'Overdrive Commercial Funding' &&
                                <LoanApprovalEstimateDisclaimer Disclaimer={translator.translate('sandhills.tradesites.mvc.overdrivelenderdisclaimer')} />}

                            {(shippingEstimateData && shippingEstimateData.estimateDisplay) &&
                                <p dangerouslySetInnerHTML={{ __html: props.ShippingEstimateDisclaimer }}></p>}
                            {showEmployeeOnlyBetaSiteToggle &&
                                <BetaSiteCookieButton ToggleBetaSiteCookie={toggleBetaSiteCookie} UseBetaSiteCookie={useBetaSiteCookie} />
                            }
                        </BaseTypography>
                    </>
                    :
                    <div className='detail__right-col'>
                        {!windowSize.isMobile &&
                            <div className='detail__heading'>
                                <div className='detail__title-container'>
                                    <h1 className='detail__title'>{props.DisplayedListingTitle || props.ListingTitle}</h1>
                                </div>
                                {props.UseSEOHTagReorganization ?
                                    <h2 className='detail__category header-reset'>{props.CategoryInformation.DisplayCategoryName}</h2>
                                    :
                                    <div className='detail__category'>{props.CategoryInformation.DisplayCategoryName}</div>
                                }
                                {props.IsPreATResult && !props.AuctionDetails?.IsAuctionResult && currentSite?.Site?.IsHostedSite &&
                                    <div className='detail__at-result'>
                                        <img className='listing-prices__additional-image' src={props.PricingInfo.preATIcon} alt="" />
                                        <span className='pre-at-result'>Pre-AuctionTime Result</span>
                                    </div>
                                }
                            </div>
                        }
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ marginBottom: '10px', fontSize: '18px' }}><strong><TranslationText tKey='sandhills.loginorregistertoviewauctionresults' /></strong></div>
                            <Grid container spacing={1} sx={{ justifyContent: 'center' }}>
                                <Button
                                    style={{ textDecoration: 'none' }}
                                    className='template-branded-button'
                                    variant='contained'
                                    sx={{ color: 'white', width: '25%', height: '36px', margin: '5px', textTransform: 'capitalize' }}
                                    href={props.LoginLink}
                                    rel={LinkRelationTypes.noFollow}>
                                    <TranslationText tKey='sandhills.sso.website.login' />
                                </Button>
                                <Button
                                    style={{ textDecoration: 'none' }}
                                    className='template-branded-button'
                                    variant='contained'
                                    sx={{ color: 'white', width: '25%', height: '36px', margin: '5px' }}
                                    href={props.RegistrationLink}
                                    rel={LinkRelationTypes.noFollow}>
                                    <TranslationText tKey='sandhills.tradepubwebsite.mvc.register' />
                                </Button>
                            </Grid>
                        </div>
                    </div>}
                {(currentSite.IsMobileAppContext || (windowSize.isMobile && props.UseBottomMobilePadding)) &&
                    <div className='detail__mobile-padding-bottom' />
                }
                {windowSize.isMobile &&
                    <div className='detail-contact-bar'>
                        {props.ShowClassicViewFlyout &&
                            <MobileModalControl ClassicViewURL={props.ClassicFlyoutModel.ClassicViewURL} ClassicViewImageURL={props.ClassicFlyoutModel.ClassicViewImageURL} />}
                        {vip &&
                            <ThemedButton id='detail-page-mobile-vip' className='contact-bar-btn black-background value-insight-portal' href={vip.VipPortalUrl} target='_blank' onClick={() => gtmPushEvent(gtmEventType.vip)}>
                                <img src={vip.VipPortalImage} alt="" width='32.5' height='12' />
                                VIP Portal
                                <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                            </ThemedButton>
                        }
                        {props.BuyNowModel?.BuyNowURL && !props.PricingInfo.ShowWholesalePriceOnly && !outOfStock &&
                            <ThemedButton
                                id='detail-page-mobile-buynow'
                                className='contact-bar-btn gtm-buynow des-buy-now'
                                href={buyNowURL}
                                target={(props.BuyNowModel.BuyNowNewTab || currentSite.IsMobileAppContext) ? '_blank' : null}
                                onClick={handleBuyNowClick}>
                                {isRY ? <i className='material-icons'>key</i> : <i className='material-icons'>shopping_cart</i>}
                                {props.BuyNowModel.BuyNowButtonText}
                                <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                            </ThemedButton>
                        }
                        {props.ContactInfo?.ShowSendAMessage &&
                            <ThemedButton
                                id='detail-page-mobile-send-a-message'
                                color={currentSite.ThemeOverride}
                                className='contact-bar-btn des-email-seller'
                                onClick={() => openThisEmailModal(inquiryType.Email, contactType.Retail, returnFocusRef.current[inquiryType.Email + '-retail-mobile'])}
                                ref={(element) => { returnFocusRef.current[inquiryType.Email + '-retail-mobile'] = element }}>
                                <i className='material-icons gtm-noclick'>mail_outline</i>
                                {props.CustomEmailSellerTextForRental ? props.CustomEmailSellerTextForRental : props.CustomEmailSellerTextForRetail ? props.CustomEmailSellerTextForRetail : props.IsForRentOnly || showEmailTextOnly ? <TranslationText tKey='sandhills.email' /> : <TranslationText tKey='sandhills.emailseller' />}
                            </ThemedButton>
                        }
                        {props.ContactInfo?.ShowContactInfo && props.ContactInfo?.ShowDealerPhoneNumber &&
                            <ThemedButton
                                id='detail-page-mobile-dealer-phone'
                                color={currentSite.ThemeOverride}
                                className='contact-bar-btn des-retail-phone'
                                ref={(element) => { returnFocusRef.current[props.ContactInfo.UnformattedPhone + '-retail-mobile-phone'] = element }}
                                onClick={(e) => {
                                    phoneClickToTrack(e, props.ContactInfo.UnformattedPhone, 'Retail Phone');
                                    gtmPushEvent(gtmEventType.call);
                                }}>
                                <a href={'tel:' + props.ContactInfo.UnformattedPhone} className='contact-bar-btn des-email-seller'>
                                    <i className='material-icons' alt='phone'>phone</i>
                                    <TranslationText tKey='sandhills.callseller' />
                                </a>
                            </ThemedButton>
                        }
                        {props.ContactInfo?.ShowCheckAvailability &&
                            <ThemedButton
                                id='detail-page-mobile-check-availability'
                                className='contact-bar-btn des-check-availability'
                                onClick={() => openThisEmailModal(inquiryType.CheckAvailability, contactType.Retail, returnFocusRef.current[inquiryType.CheckAvailability])}
                                ref={(element) => { returnFocusRef.current[inquiryType.CheckAvailability + '-mobile'] = element }} >
                                <Icon className='fas fa-check' />
                                <TranslationText tKey='sandhills.checkavailability' />
                            </ThemedButton>}
                        {currentSite.IsBetaSite &&
                            <ThemedButton
                                id='detail-page-mobile-watchlist-add'
                                className='contact-bar-btn watchlist-add'
                                onClick={() => forceExpansion()}
                            >
                                <Icon baseClassName={isSavedToWatchlist ? 'fa-solid' : 'far'} className='fa-heart detail__watchlist-icon' />
                                {isSavedToWatchlist ? <span>Remove From Watchlist</span> : <TranslationText tKey='sandhills.tradepubwebsite.mvc.addtowatchlist' />}
                            </ThemedButton>
                        }
                    </div>}

                {showFinancialCalculator &&
                    <CalculatorModal
                    toggleCalculator={toggleCalculator}
                    ShowMonthlyFinanceRatesOnListings={props.Widgets.ShowMonthlyFinanceRatesOnListings}
                    LenderImageUrl={props.Widgets.LenderImageURL}
                    LenderUrl={props.Widgets.LenderWidgetURLFinCalc}
                    LenderCompanyName={props.Widgets.LenderCompanyName}
                    Price={props.PricingInfo.RetailPrice}
                    Currencies={props.PricingInfo.CurrencySelector.Currencies}
                    DefaultCurrency={props.DefaultCurrencyCode}
                    DefaultCultureCode={props.CultureCodeForCurrency}
                    PaymentsAsLowAsTerm={props.Widgets.PaymentsAsLowAsTerm}
                    PaymentsAsLowAsInterestRate={props.Widgets.PaymentsAsLowAsInterestRate}
                    />}

                {props.SAMModel && showEmailModal &&
                    <EmailModal
                        {...samModel}
                        CurrencySelector={props.PricingInfo.CurrencySelector}
                        toggleEmailModal={onEmailModalToggle}
                        selectedInquiry={selectedInquiry}
                        selectedContactType={selectedContactType}
                        DefaultCultureCode={props.CultureCodeForCurrency}
                        ShouldPushCriteoEvent={props.ShouldPushCriteoEvent}
                        ShowSocialShareButtons={showShareButtons}
                        IsPreAT={isPreATModal}
                        IsWholeSale={isWholesaleModal}
                        Ref={returnFocusRef}
                        useWholesaleOverride={useWholesaleOverride}
                        WholesaleCRMID={props.ContactInfo.WholesaleCRMID}
                        WholesaleDG={props.ContactInfo.WholesaleDG}
                        IsPreTrade={props.IsPreTrade}
                    />}

                {props.LoanApprovalEstimateModel?.ShowBanner &&
                    <LoanApprovalEstimateModal {...props.LoanApprovalEstimateModel} />}

                {showBidHistoryModal &&
                    <BidHistoryModal
                        bidHistory={bidHistoryData}
                        onClose={(e) => { setShowBidHistoryModal(false); }}
                    />}

                {showWinningBidderModal &&
                    <WinningBidderModal
                        winningBidder={winningBidderData}
                        onClose={(e) => { setShowWinningBidderModal(false); }}
                    />}

                <Backdrop
                    open={isLoading}
                    sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                    <CircularProgress />
                </Backdrop>
            </main >
        </div >
    );
};

export default DetailsView;
global.DetailsView = DetailsView;